// kernel/useLayers.ts
import { useCallback, RefObject, useMemo, useRef } from 'react';
import { GenerationChange, KernelHandle, useLayersReturnType, Rect, Layer, Workspace, DiffusionParams, LayerModificationLogEntry, CropPoint, InventoryEntityState, NativeLayerCutOptions, getLayerWorkspaceId, listWorkspaceLayers, normalizeLayerWorkspaceId } from  '#types/types';
import { copyTextToClipboard } from '#lib/utils';
import {
  extractFramesFromVideo,
  loadImageDataFromUrl,
  loadVideoBlobAndMetadata,
  getColorHarmonizationAnalysisDimensions,
  MAX_COLOR_HARMONIZATION_ANALYSIS_PIXELS,
  resizeImageDataToPixelBudget,
} from '#lib/canvas-utils';
import { uploadFileToCloud } from '#lib/azure-client';
import { modelMappings } from '@/components/ModelSelector';
import { getWorkspaceRasterDimensions } from '@/components/workspaceRasterUtils';
import cvReadyPromise from "@techstark/opencv-js";
import { toast } from 'sonner';
import { getDefaultParamsForModel } from '#lib/models';
import { getNextRenderOrderForWorkspace, resequenceRenderOrderByArray, sortLayersByRenderOrder } from '#lib/layer-render-order';
import { firstLayerFrameSize, syncRasterLayerRectSize } from '#lib/layer-geometry';
import {
  clampNativeLayerCutBlendRadius,
  computeLayerRaster,
  getNativeLayerCutImageBounds,
} from '#lib/layer-cut-utils';
import { runLayerRasterInWorker } from '#lib/layer-raster-client';
import { describeBuriedLayerStack, findBuriedLayerIdsOfStack } from '#lib/buried-layer-stack';
import { AUTO_CUT_BLEND_RADIUS_PX, AUTO_CUT_NOTHING_KEPT, MAX_AUTO_CUT_ANALYSIS_PIXELS } from '#lib/auto-cut';
import { matrixToApply } from '#lib/opencv-tiled-alignment';
import { runAutoCutExtractionWithFallback, runDriftHarmonizationWithFallback, runLayerAlignmentWithFallback } from '#lib/opencv-worker-client';
import { layerChangeDimensions, measureLayerChange } from '#lib/drift-harmonization';
import { requireWorkspaceProjectIdFromKernel } from '#lib/workspace-scope';
import { ALIGN_SKIPPED, coversWholeFrame, keepFrameEdgesCovered, LayerCorrectionRefusal, runLayerIntegration } from '#lib/layer-integration';
import { MIN_TIMELINE_TRACK_SPAN_MS, toFiniteTimelineMs } from '#lib/workspace-timeline';
import {
  SVG_LAYER_PARAM_KEY,
  loadSvgIntrinsicSizeFromUrl,
  parseSvgIntrinsicSize,
} from '#lib/svg-layer';
import { getDrawingAwareSvgLayerSource, getDrawingLayerPayload } from '#lib/drawing-layer';
import { performColorHarmonization } from '#lib/color-harmonization';
import {
  getProjectDraggedLayerId,
  getProjectIsResizingLayer,
  getProjectPointBeingDragged,
  getProjectSelectedLayersId,
  setProjectDraggedLayerId,
  setProjectIsResizingLayer,
  setProjectPointBeingDragged,
  setProjectSelectedLayersId,
} from '#lib/project-runtime-layer-ui-state';
import { reconcileWorkspaceVersionsAfterLayerRemoval, syncWorkspaceRuntimeVersionsFromInventory } from '@/kernel/useVersions';
import { resolveGenerationOutputScopeForView } from '#lib/generation-output-scope';
import { getProjectStudioViewMode } from '#lib/studio-project-view';
import { hydrateProjectLayerPreviews as runProjectLayerPreviewHydration } from '#lib/project-layer-preview';
import { signImageExport } from '#lib/provenance/signImageExport';
import { STUDIO_LAYER_DTO_AUTOSAVE_SCOPE } from '#lib/studio-runtime-dto';
import {
  buildStudioLayerRectPreservingTransformPivot,
  getTransformedRectBounds,
  mergeStudioLayerTransformParams,
  projectStudioLayerLocalRectToWorld,
  resolveStudioLayerTransform,
  reprojectStudioLayerWorldImageToLocal,
  drawImageWithStudioLayerTransform,
  STUDIO_LAYER_MAX_SIDE_PIXELS,
} from '#lib/studio-layer-transform';

const MAX_PARALLEL_LAYER_HYDRATION = 12;
const DEFAULT_BLEND_RADIUS_PX = 16;
// Default lifetime of an image-like layer = one frame (T1 = T0 + 1 frame).
const DEFAULT_IMAGE_LIKE_TIMELINE_DURATION_MS = MIN_TIMELINE_TRACK_SPAN_MS;

const now = typeof performance !== 'undefined' && performance.now ? () => performance.now() : () => Date.now();
const roundMs = (value: number) => Number(value.toFixed(2));

const createLayerId = (): string => `layer-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const hasExplicitLayerTimelineTiming = (partialLayer: Partial<Layer>): boolean => (
  Object.prototype.hasOwnProperty.call(partialLayer, 'timelineStartMs') ||
  Object.prototype.hasOwnProperty.call(partialLayer, 'timelineEndMs') ||
  Object.prototype.hasOwnProperty.call(partialLayer, 'timelineSourceInMs')
);

const isImageLikeLayerType = (type: unknown): boolean => (
  type !== 'video' && type !== 'audio'
);

export interface ExternalStudioLayerDtoChange {
  entityId: string;
  eventType: 'CREATE' | 'UPDATE' | 'DELETE';
  previousEntity: Pick<InventoryEntityState<'StudioLayer'>, 'visible' | 'projectId' | 'workspaceId' | 'type' | 'imageSrc' | 'videoSrc'> | null;
  nextEntity: Pick<InventoryEntityState<'StudioLayer'>, 'visible' | 'projectId' | 'workspaceId' | 'type' | 'imageSrc' | 'videoSrc'> | null;
}

export interface ExternalStudioWorkspaceDtoChange {
  entityId: string;
  eventType: 'CREATE' | 'UPDATE' | 'DELETE';
  previousEntity: Pick<InventoryEntityState<'StudioWorkspace'>, 'frameRect' | 'layerIds' | 'projectId'> | null;
  nextEntity: Pick<InventoryEntityState<'StudioWorkspace'>, 'frameRect' | 'layerIds' | 'projectId'> | null;
}

const isDtoLayerVisible = (
  layer: Pick<InventoryEntityState<'StudioLayer'>, 'visible' | 'projectId' | 'workspaceId'> | null
): boolean => Boolean(layer && layer.visible !== false);

const isDtoLayerWorkspaceScoped = (
  layer: Pick<InventoryEntityState<'StudioLayer'>, 'workspaceId'> | null
): boolean => typeof layer?.workspaceId === 'string' && layer.workspaceId.trim().length > 0;

const resolveDtoLayerProjectId = (
  layer: Pick<InventoryEntityState<'StudioLayer'>, 'visible' | 'projectId' | 'workspaceId' | 'type' | 'imageSrc' | 'videoSrc'> | null
): string => {
  if (!layer || typeof layer.projectId !== 'string') return '';
  return layer.projectId.trim();
};

const normalizeLayerType = (
  layer: Pick<InventoryEntityState<'StudioLayer'>, 'visible' | 'projectId' | 'workspaceId' | 'type' | 'imageSrc' | 'videoSrc'> | null
): 'video' | 'audio' | 'svg' | 'image' => {
  const type = layer?.type;
  if (type === 'video') return 'video';
  if (type === 'audio') return 'audio';
  if (type === 'svg') return 'svg';
  return 'image';
};

const readTrimmedLayerSource = (
  value: unknown
): string => (typeof value === 'string' ? value.trim() : '');

const resolveHydrationSourceFingerprint = (
  layer: Pick<InventoryEntityState<'StudioLayer'>, 'visible' | 'projectId' | 'workspaceId' | 'type' | 'imageSrc' | 'videoSrc'> | null
): string => {
  if (!layer) return '';
  const kind = normalizeLayerType(layer);
  if (kind === 'video' || kind === 'audio') {
    const source = readTrimmedLayerSource(layer.videoSrc);
    return source ? `${kind}:${source}` : '';
  }
  if (kind === 'svg') {
    const source = readTrimmedLayerSource(layer.imageSrc);
    return source ? `svg:${source}` : 'svg:inline';
  }
  const source = readTrimmedLayerSource(layer.imageSrc);
  return source ? `image:${source}` : '';
};

const isLayerHydrationReady = (
  layer: Pick<InventoryEntityState<'StudioLayer'>, 'visible' | 'projectId' | 'workspaceId' | 'type' | 'imageSrc' | 'videoSrc'> | null
): boolean => {
  if (!isDtoLayerWorkspaceScoped(layer) || !isDtoLayerVisible(layer)) return false;
  return resolveHydrationSourceFingerprint(layer).length > 0;
};

const areStringArraysEqual = (left: string[], right: string[]): boolean => {
  if (left.length !== right.length) return false;
  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) return false;
  }
  return true;
};

const readProjectRuntimeUIWorkspaceId = (
  kernelHandle: KernelHandle,
  projectId: string
): string => {
  const value = kernelHandle.readProjectRuntimeState(projectId).UIWorkspaceId;
  return typeof value === 'string' ? value.trim() : '';
};

const healProjectSceneRuntimeAfterExternalDtoChanges = ({
  kernelHandle,
  projectId,
}: {
  kernelHandle: KernelHandle;
  projectId: string;
}): void => {
  const normalizedProjectId = projectId.trim();
  if (!normalizedProjectId) return;

  const inventory = kernelHandle.getInventory();
  const project = inventory.Project.registry[normalizedProjectId];
  if (!project) return;

  const workspaceRegistry = inventory.StudioWorkspace.registry;
  const layerRegistry = inventory.StudioLayer.registry;
  const projectWorkspaceIds = Array.isArray(project.workspaceIds)
    ? project.workspaceIds
      .filter((workspaceId): workspaceId is string => typeof workspaceId === 'string')
      .map((workspaceId) => workspaceId.trim())
      .filter((workspaceId) => workspaceId.length > 0 && Boolean(workspaceRegistry[workspaceId]))
    : [];
  const selectionBefore = getProjectSelectedLayersId(kernelHandle, normalizedProjectId);

  if (projectWorkspaceIds.length === 0) {
    if (selectionBefore.length > 0) {
      setProjectSelectedLayersId(kernelHandle, normalizedProjectId, []);
    }
    setProjectDraggedLayerId(kernelHandle, normalizedProjectId, null);
    setProjectPointBeingDragged(kernelHandle, normalizedProjectId, null);
    if (getProjectIsResizingLayer(kernelHandle, normalizedProjectId)) {
      setProjectIsResizingLayer(kernelHandle, normalizedProjectId, false);
    }
    const runtimeWorkspaceId = readProjectRuntimeUIWorkspaceId(kernelHandle, normalizedProjectId);
    if (runtimeWorkspaceId) {
      kernelHandle.setProjectUIWorkspaceId(normalizedProjectId, '');
    }
    return;
  }

  const resolvedWorkspaceId = kernelHandle.getProjectUIWorkspaceId(normalizedProjectId);
  const activeWorkspaceId = projectWorkspaceIds.includes(resolvedWorkspaceId)
    ? resolvedWorkspaceId
    : (projectWorkspaceIds[0] ?? '');
  if (!activeWorkspaceId) return;
  const runtimeWorkspaceId = readProjectRuntimeUIWorkspaceId(kernelHandle, normalizedProjectId);
  if (runtimeWorkspaceId !== activeWorkspaceId) {
    kernelHandle.setProjectUIWorkspaceId(normalizedProjectId, activeWorkspaceId);
  }

  const activeWorkspace = workspaceRegistry[activeWorkspaceId];
  if (!activeWorkspace) {
    setProjectDraggedLayerId(kernelHandle, normalizedProjectId, null);
    setProjectPointBeingDragged(kernelHandle, normalizedProjectId, null);
    if (getProjectIsResizingLayer(kernelHandle, normalizedProjectId)) {
      setProjectIsResizingLayer(kernelHandle, normalizedProjectId, false);
    }
    return;
  }

  const activeWorkspaceLayerIds = normalizeExternalWorkspaceLayerIds(activeWorkspace);
  const validActiveWorkspaceLayerIds = new Set<string>();
  activeWorkspaceLayerIds.forEach((layerId) => {
    const layer = layerRegistry[layerId];
    if (!layer) return;
    const layerWorkspaceId = typeof layer.workspaceId === 'string' ? layer.workspaceId.trim() : '';
    if (layerWorkspaceId !== activeWorkspaceId) return;
    validActiveWorkspaceLayerIds.add(layerId);
  });

  let interactionStateWasCleared = false;
  const draggedLayerId = getProjectDraggedLayerId(kernelHandle, normalizedProjectId);
  if (draggedLayerId && !validActiveWorkspaceLayerIds.has(draggedLayerId)) {
    setProjectDraggedLayerId(kernelHandle, normalizedProjectId, null);
    interactionStateWasCleared = true;
  }
  const pointBeingDragged = getProjectPointBeingDragged(kernelHandle, normalizedProjectId);
  if (pointBeingDragged && !validActiveWorkspaceLayerIds.has(pointBeingDragged.layerId)) {
    setProjectPointBeingDragged(kernelHandle, normalizedProjectId, null);
    interactionStateWasCleared = true;
  }
  if (interactionStateWasCleared && getProjectIsResizingLayer(kernelHandle, normalizedProjectId)) {
    setProjectIsResizingLayer(kernelHandle, normalizedProjectId, false);
  }

  if (selectionBefore.length === 0) return;
  const selectionAfter = selectionBefore.filter((layerId) => validActiveWorkspaceLayerIds.has(layerId));
  if (!areStringArraysEqual(selectionBefore, selectionAfter)) {
    setProjectSelectedLayersId(kernelHandle, normalizedProjectId, selectionAfter);
  }
};

export const handleExternalStudioLayerDtoChanges = ({
  kernel,
  changes,
}: {
  kernel: RefObject<KernelHandle | null>;
  changes: ExternalStudioLayerDtoChange[];
}): void => {
  const kernelHandle = kernel.current;
  if (!kernelHandle || changes.length === 0) return;

  const projectsToHydrate = new Set<string>();
  const projectsToHealRuntime = new Set<string>();

  changes.forEach((change) => {
    const projectId =
      resolveDtoLayerProjectId(change.nextEntity) ||
      resolveDtoLayerProjectId(change.previousEntity);
    const previousWorkspaceScoped = isDtoLayerWorkspaceScoped(change.previousEntity);
    const nextWorkspaceScoped = isDtoLayerWorkspaceScoped(change.nextEntity);
    if (projectId && (previousWorkspaceScoped || nextWorkspaceScoped)) {
      projectsToHealRuntime.add(projectId);
    }
    if (change.eventType === 'DELETE' || !nextWorkspaceScoped) return;
    const becameVisible = !isDtoLayerVisible(change.previousEntity) && isDtoLayerVisible(change.nextEntity);
    const previousHydrationReady = isLayerHydrationReady(change.previousEntity);
    const nextHydrationReady = isLayerHydrationReady(change.nextEntity);
    const becameHydrationReady = !previousHydrationReady && nextHydrationReady;
    const sourceFingerprintChanged =
      nextHydrationReady &&
      resolveHydrationSourceFingerprint(change.previousEntity) !==
        resolveHydrationSourceFingerprint(change.nextEntity);
    const shouldHydrate =
      becameVisible ||
      becameHydrationReady ||
      (change.eventType === 'UPDATE' && sourceFingerprintChanged);
    if (!shouldHydrate) return;
    if (!projectId) return;
    projectsToHydrate.add(projectId);
  });

  projectsToHydrate.forEach((projectId) => {
    kernelHandle.hydrateVisibleLayers(projectId);
  });
  projectsToHealRuntime.forEach((projectId) => {
    healProjectSceneRuntimeAfterExternalDtoChanges({
      kernelHandle,
      projectId,
    });
  });
};

const normalizeExternalFrameRect = (
  workspace: Pick<InventoryEntityState<'StudioWorkspace'>, 'frameRect'> | null
): Rect | null => {
  const frameRect = workspace?.frameRect;
  if (!frameRect || typeof frameRect !== 'object') return null;
  const x = Reflect.get(frameRect, 'x');
  const y = Reflect.get(frameRect, 'y');
  const w = Reflect.get(frameRect, 'w');
  const h = Reflect.get(frameRect, 'h');
  if (
    typeof x !== 'number' || !Number.isFinite(x) ||
    typeof y !== 'number' || !Number.isFinite(y) ||
    typeof w !== 'number' || !Number.isFinite(w) || w <= 0 ||
    typeof h !== 'number' || !Number.isFinite(h) || h <= 0
  ) {
    return null;
  }
  return { x, y, w, h };
};

const areRectsEqual = (left: Rect, right: Rect): boolean =>
  left.x === right.x && left.y === right.y && left.w === right.w && left.h === right.h;

const normalizeExternalWorkspaceLayerIds = (
  workspace: Pick<InventoryEntityState<'StudioWorkspace'>, 'layerIds'> | null
): string[] => {
  const layerIds = workspace?.layerIds;
  if (!Array.isArray(layerIds)) return [];
  return layerIds
    .filter((layerId): layerId is string => typeof layerId === 'string' && layerId.trim().length > 0)
    .map((layerId) => layerId.trim());
};

const resolveExternalWorkspaceProjectId = (
  workspace: Pick<InventoryEntityState<'StudioWorkspace'>, 'projectId'> | null
): string => {
  if (!workspace || typeof workspace.projectId !== 'string') return '';
  return workspace.projectId.trim();
};

const workspaceLayerIdsGainedEntries = ({
  previousEntity,
  nextEntity,
}: {
  previousEntity: Pick<InventoryEntityState<'StudioWorkspace'>, 'layerIds'> | null;
  nextEntity: Pick<InventoryEntityState<'StudioWorkspace'>, 'layerIds'> | null;
}): boolean => {
  const previousLayerIds = normalizeExternalWorkspaceLayerIds(previousEntity);
  const nextLayerIds = normalizeExternalWorkspaceLayerIds(nextEntity);
  if (nextLayerIds.length === 0) return false;
  if (previousLayerIds.length === 0) return true;

  const previousSet = new Set(previousLayerIds);
  return nextLayerIds.some((layerId) => !previousSet.has(layerId));
};

const reprojectExternalAnnotationMaskForFrameRectChange = ({
  kernelHandle,
  workspaceId,
  previousFrameRect,
  nextFrameRect,
}: {
  kernelHandle: KernelHandle;
  workspaceId: string;
  previousFrameRect: Rect;
  nextFrameRect: Rect;
}): void => {
  if (typeof document === 'undefined' || typeof ImageData === 'undefined') return;

  const workspace = kernelHandle.getInventory().StudioWorkspace.registry[workspaceId];
  if (!workspace) return;
  const existingAnnotationMask = workspace.annotationMask;
  if (!(existingAnnotationMask instanceof ImageData)) return;
  const activeVersionId =
    typeof workspace.activeVersionId === 'string' && workspace.activeVersionId.trim().length > 0
      ? workspace.activeVersionId
      : (typeof workspace.validatedVersionId === 'string' && workspace.validatedVersionId.trim().length > 0
        ? workspace.validatedVersionId
        : null);
  if (!activeVersionId) return;

  const { width: nextMaskWidth, height: nextMaskHeight, scale: nextMaskScale } =
    getWorkspaceRasterDimensions(nextFrameRect);
  const { scale: previousMaskScale } = getWorkspaceRasterDimensions(previousFrameRect);
  if (
    nextMaskWidth <= 0 ||
    nextMaskHeight <= 0 ||
    !Number.isFinite(nextMaskScale) ||
    nextMaskScale <= 0 ||
    !Number.isFinite(previousMaskScale) ||
    previousMaskScale <= 0
  ) {
    return;
  }

  const nextMaskCanvas = document.createElement('canvas');
  nextMaskCanvas.width = nextMaskWidth;
  nextMaskCanvas.height = nextMaskHeight;
  const nextMaskCtx = nextMaskCanvas.getContext('2d');
  if (!nextMaskCtx) return;

  const previousMaskCanvas = document.createElement('canvas');
  previousMaskCanvas.width = existingAnnotationMask.width;
  previousMaskCanvas.height = existingAnnotationMask.height;
  const previousMaskCtx = previousMaskCanvas.getContext('2d');
  if (!previousMaskCtx) return;
  previousMaskCtx.putImageData(existingAnnotationMask, 0, 0);

  const dx = nextFrameRect.x - previousFrameRect.x;
  const dy = nextFrameRect.y - previousFrameRect.y;
  const translateX = -dx * nextMaskScale;
  const translateY = -dy * nextMaskScale;
  const scaleRatio = nextMaskScale / previousMaskScale;

  nextMaskCtx.translate(translateX, translateY);
  nextMaskCtx.scale(scaleRatio, scaleRatio);
  nextMaskCtx.imageSmoothingEnabled = false;
  nextMaskCtx.drawImage(previousMaskCanvas, 0, 0);

  const reprojectedMask = nextMaskCtx.getImageData(0, 0, nextMaskWidth, nextMaskHeight);
  kernelHandle.patchEntityRuntimeState('StudioWorkspace', workspaceId, {
    annotationMask: reprojectedMask,
    annotationMaskVersionId: activeVersionId,
    versions: (workspace.versions ?? []).map((version) => {
      if (version.id !== activeVersionId) return version;
      return {
        ...version,
        annotationMask: reprojectedMask,
        annotationMaskIsDirty: true,
      };
    }),
  });
};

export const handleExternalStudioWorkspaceDtoChanges = ({
  kernel,
  changes,
}: {
  kernel: RefObject<KernelHandle | null>;
  changes: ExternalStudioWorkspaceDtoChange[];
}): void => {
  const kernelHandle = kernel.current;
  if (!kernelHandle || changes.length === 0) return;
  const projectsToHydrate = new Set<string>();
  const projectsToHealRuntime = new Set<string>();
  const workspacesToSyncVersions = new Set<string>();

  changes.forEach((change) => {
    const previousFrameRect = normalizeExternalFrameRect(change.previousEntity);
    const nextFrameRect = normalizeExternalFrameRect(change.nextEntity);
    const layerIdsGained = change.eventType !== 'DELETE'
      ? workspaceLayerIdsGainedEntries({
          previousEntity: change.previousEntity,
          nextEntity: change.nextEntity,
        })
      : false;
    const projectIdFromChange =
      resolveExternalWorkspaceProjectId(change.nextEntity) ||
      resolveExternalWorkspaceProjectId(change.previousEntity);
    if (projectIdFromChange) {
      projectsToHealRuntime.add(projectIdFromChange);
    }
    if (change.eventType !== 'DELETE') {
      workspacesToSyncVersions.add(change.entityId);
    }

    if (change.eventType !== 'DELETE') {
      if (layerIdsGained) {
        const projectId = projectIdFromChange;
        if (projectId) {
          projectsToHydrate.add(projectId);
        }
      }
    }

    if (change.eventType === 'UPDATE') {
      if (!previousFrameRect || !nextFrameRect) return;
      if (areRectsEqual(previousFrameRect, nextFrameRect)) return;

      reprojectExternalAnnotationMaskForFrameRectChange({
        kernelHandle,
        workspaceId: change.entityId,
        previousFrameRect,
        nextFrameRect,
      });
    }
  });

  projectsToHydrate.forEach((projectId) => {
    kernelHandle.hydrateVisibleLayers(projectId);
  });
  workspacesToSyncVersions.forEach((workspaceId) => {
    syncWorkspaceRuntimeVersionsFromInventory({
      kernelHandle,
      workspaceId,
    });
  });
  projectsToHealRuntime.forEach((projectId) => {
    healProjectSceneRuntimeAfterExternalDtoChanges({
      kernelHandle,
      projectId,
    });
  });
};

const cloneModificationLog = (log?: LayerModificationLogEntry[]) =>
  log ? log.map(entry => ({ ...entry })) : undefined;

const mergeModificationLogs = (
  existing?: LayerModificationLogEntry[],
  fallback?: LayerModificationLogEntry[],
  entries?: LayerModificationLogEntry[]
): LayerModificationLogEntry[] | undefined => {
  const base = existing ?? fallback ?? [];
  const result: LayerModificationLogEntry[] = base.map(entry => ({ ...entry }));
  if (entries && entries.length) {
    result.push(...entries.map(entry => ({ ...entry })));
  }
  return result.length ? result : undefined;
};

const LAYER_CORRECTION_KEYS = ['imageData', 'rect', 'scale', 'contentVersion', 'imageSrc'] as const;
type LayerCorrection = Partial<Pick<Layer, typeof LAYER_CORRECTION_KEYS[number]>>;

// Only the fields the correction changes: restoring the others on undo would revert edits it never made.
const snapshotLayerCorrection = (layer: Layer, next: LayerCorrection): LayerCorrection => {
  const snapshot: LayerCorrection = {};
  for (const key of LAYER_CORRECTION_KEYS) {
    if (key in next) Object.assign(snapshot, { [key]: key === 'rect' ? { ...layer.rect } : layer[key] });
  }
  return snapshot;
};

const applyLayerCorrectionInMemory = (layer: Layer, next: LayerCorrection, type: LayerModificationLogEntry['type']): Layer => ({
  ...layer,
  ...next,
  modificationLog: mergeModificationLogs(layer.modificationLog, undefined, [{ timestamp: new Date().toISOString(), type }]),
});

/** The corrections an integration actually applied, the log entries its layer carries. */
const integrationLogTypes = ({ alignSkipped, harmonizeSkipped }: { alignSkipped: boolean; harmonizeSkipped: boolean }): LayerModificationLogEntry['type'][] => [
  ...(alignSkipped ? [] : ['MANUAL_ALIGN' as const]),
  ...(harmonizeSkipped ? [] : ['MANUAL_UNCHANGED_HARMONIZE' as const]),
];

const toLayerRenderRequest = (layer: Layer, fromMemory: boolean): { layerIds: string[]; extraLayers?: Layer[] } => {
  if (!fromMemory) return { layerIds: [layer.id] };
  const candidate = { ...layer, id: `${layer.id}:candidate` };
  return { layerIds: [candidate.id], extraLayers: [candidate] };
};

const LEGACY_PLACEHOLDER_RECT_SIZE = 512;
const isLegacyPlaceholderRectSize = (value: number | null): boolean =>
  typeof value === 'number' &&
  Number.isFinite(value) &&
  Math.abs(value - LEGACY_PLACEHOLDER_RECT_SIZE) <= 1e-6;

const isLegacyInvalidRasterRect = (rect: Rect): boolean => {
  const rectW = typeof rect.w === 'number' && Number.isFinite(rect.w) ? rect.w : 0;
  const rectH = typeof rect.h === 'number' && Number.isFinite(rect.h) ? rect.h : 0;
  if (rectW <= 0 || rectH <= 0) return true;
  return isLegacyPlaceholderRectSize(rectW) && isLegacyPlaceholderRectSize(rectH);
};

const resolveRasterLayerScaleForHydration = (
  layer: Layer,
  intrinsicWidth: number,
  intrinsicHeight: number
): number => {
  const existingScale =
    typeof layer.scale === 'number' && Number.isFinite(layer.scale) && layer.scale > 0
      ? layer.scale
      : null;
  const hasLegacyInvalidRect = isLegacyInvalidRasterRect(layer.rect);

  if (hasLegacyInvalidRect && existingScale !== null) {
    return existingScale;
  }
  if (hasLegacyInvalidRect) {
    throw new Error(
      `[useLayers.resolveRasterLayerScaleForHydration] Missing explicit scale for suspicious rect on layer "${layer.id}" (rect=${JSON.stringify(layer.rect)}, intrinsic=${intrinsicWidth}x${intrinsicHeight}).`
    );
  }

  if (!hasLegacyInvalidRect && Number.isFinite(layer.rect.w) && layer.rect.w > 0 && intrinsicWidth > 0) {
    return layer.rect.w / intrinsicWidth;
  }

  if (existingScale !== null) {
    return existingScale;
  }

  throw new Error(
    `[useLayers.resolveRasterLayerScaleForHydration] Missing width scale inference for layer "${layer.id}" (rect=${JSON.stringify(layer.rect)}, intrinsic=${intrinsicWidth}x${intrinsicHeight}).`
  );
};

interface useLayersProps {
  kernel: RefObject<KernelHandle | null>;
}

export const useLayers = ({ kernel }: useLayersProps): useLayersReturnType => {
  const projectPreviewInflightLayerIds = useRef(new Set<string>());
  const projectPreviewCacheEntries = useRef(new Map<string, { bytes: number; lastUsed: number }>());
  const projectPreviewDeferredLayerSources = useRef(new Map<string, string>());

  const normalizeLayerIdentifier = useCallback((value: unknown): string => {
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    return '';
  }, []);

  const toFiniteNumber = useCallback((value: unknown): number | null => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim().length > 0) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
    return null;
  }, []);

  const readLayerPulseToken = useCallback((layerId: string): number => {
    const k = kernel.current;
    const normalizedLayerId = typeof layerId === 'string' ? layerId.trim() : '';
    if (!k || !normalizedLayerId) return 0;
    const layer = k.getInventory().StudioLayer.registry[normalizedLayerId];
    return typeof layer?.pulseToken === 'number' && Number.isFinite(layer.pulseToken)
      ? layer.pulseToken
      : 0;
  }, [kernel]);

  const bumpLayerPulseToken = useCallback((layerId: string): void => {
    const k = kernel.current;
    const normalizedLayerId = typeof layerId === 'string' ? layerId.trim() : '';
    if (!k || !normalizedLayerId) return;
    const previous = readLayerPulseToken(normalizedLayerId);
    k.patchEntityRuntimeState('StudioLayer', normalizedLayerId, {
      pulseToken: previous + 1,
    });
  }, [kernel, readLayerPulseToken]);

  const reconcileWorkspaceVersionsForDetachedLayer = useCallback((workspace: Workspace, removedLayerId: string): Workspace => {
    return reconcileWorkspaceVersionsAfterLayerRemoval(workspace, removedLayerId);
  }, []);

  const attachLayerToWorkspace = useCallback((workspaceId: string, layerId: string) => {
    if (!kernel.current) return;
    const normalizedWorkspaceId = workspaceId.trim();
    const normalizedLayerId = layerId.trim();
    if (!normalizedWorkspaceId || !normalizedLayerId) return;
    const projectId = requireWorkspaceProjectIdFromKernel(kernel, normalizedWorkspaceId, 'useLayers');
    kernel.current.setWorkspace(normalizedWorkspaceId, (workspace) => {
      const existingLayerIds = Array.isArray(workspace.layerIds)
        ? workspace.layerIds.filter((id): id is string => typeof id === 'string' && id.trim().length > 0)
        : [];
      if (existingLayerIds.includes(normalizedLayerId)) {
        return workspace;
      }
      return {
        ...workspace,
        layerIds: [...existingLayerIds, normalizedLayerId],
      };
    }, projectId);
    kernel.current.setLayer(normalizedLayerId, (layer) => ({
      ...layer,
      layerWorkspaceId: normalizedWorkspaceId,
      mustRender: true,
    }));
    kernel.current.invalidateWorkspaceVersionCache(normalizedWorkspaceId);
  }, [kernel]);

  const detachLayerFromWorkspace = useCallback((workspaceId: string, layerId: string) => {
    if (!kernel.current) return;
    const normalizedWorkspaceId = workspaceId.trim();
    const normalizedLayerId = layerId.trim();
    if (!normalizedWorkspaceId || !normalizedLayerId) return;
    const projectId = requireWorkspaceProjectIdFromKernel(kernel, normalizedWorkspaceId, 'useLayers');

    kernel.current.setWorkspace(normalizedWorkspaceId, (workspace) => {
      const existingLayerIds = Array.isArray(workspace.layerIds)
        ? workspace.layerIds.filter((id): id is string => typeof id === 'string' && id.trim().length > 0)
        : [];
      const nextWorkspace = {
        ...workspace,
        layerIds: existingLayerIds.filter((id) => id !== normalizedLayerId),
      };
      return reconcileWorkspaceVersionsForDetachedLayer(nextWorkspace, normalizedLayerId);
    }, projectId);
    kernel.current.setLayer(normalizedLayerId, (layer) => ({
      ...layer,
      layerWorkspaceId: null,
      mustRender: true,
    }));
    kernel.current.invalidateWorkspaceVersionCache(normalizedWorkspaceId);
  }, [kernel, reconcileWorkspaceVersionsForDetachedLayer]);

  const getLayerById = useCallback((layerId: string): Layer | null => {
    if (!kernel.current) return null;
    return (kernel.current.getLayerRegistry()[layerId]) ?? null;
  }, [kernel]);

  const getLayersInWorkspaceContext = useCallback((workspaceId: string | null | undefined): Layer[] => {
    if (!kernel.current || !workspaceId) return [];
    const k = kernel.current;
    const workspace = k.getWorkspace(workspaceId);
    const projectId = workspace?.projectId?.trim() ?? '';
    if (!projectId) return [];
    const layers = listWorkspaceLayers(Object.values(k.getLayerRegistry()), projectId, workspaceId);
    const layerIds = new Set(workspace?.layerIds ?? []);
    return sortLayersByRenderOrder(layers.filter((layer) => layerIds.has(layer.id)));
  }, [kernel]);

  const listLayersByProjectId = useCallback((projectIdInput: string): Layer[] => {
    if (!kernel.current) return [];
    const projectId = projectIdInput.trim();
    if (!projectId) return [];

    const inventory = kernel.current.getInventory();
    const project = inventory.Project.registry[projectId];
    const workspaceIds = Array.isArray(project?.workspaceIds)
      ? project.workspaceIds.filter(
          (workspaceId): workspaceId is string =>
            typeof workspaceId === 'string' && workspaceId.trim().length > 0
        )
      : [];

    const layerRegistry = kernel.current.getLayerRegistry();
    const layers = workspaceIds.flatMap((workspaceId) =>
      listWorkspaceLayers(Object.values(layerRegistry), projectId, workspaceId)
    );
    return sortLayersByRenderOrder(layers);
  }, [kernel]);

  const syncWorkspaceLayerMembership = useCallback((workspaceIdsInput: string[]) => {
    if (!kernel.current) return;
    const k = kernel.current;

    const workspaceIds = Array.from(
      new Set(
        workspaceIdsInput
          .map((workspaceId) => (typeof workspaceId === 'string' ? workspaceId.trim() : ''))
          .filter((workspaceId) => workspaceId.length > 0)
      )
    );
    if (workspaceIds.length === 0) return;

    const layersByWorkspaceId = new Map<string, Layer[]>();
    workspaceIds.forEach((workspaceId) => {
      layersByWorkspaceId.set(workspaceId, []);
    });

    Object.values(k.getLayerRegistry()).forEach((layer) => {
      const workspaceId = getLayerWorkspaceId(layer);
      if (!workspaceId) return;
      const workspace = k.getWorkspace(workspaceId);
      const layerProjectId = typeof layer.projectId === 'string' ? layer.projectId.trim() : '';
      if (!workspace || !layerProjectId || workspace.projectId.trim() !== layerProjectId) return;
      const bucket = layersByWorkspaceId.get(workspaceId);
      if (!bucket) return;
      bucket.push(layer);
    });

    workspaceIds.forEach((workspaceId) => {
      const workspace = k.getWorkspace(workspaceId);
      if (!workspace) return;

      const nextLayerIds = sortLayersByRenderOrder(layersByWorkspaceId.get(workspaceId) ?? []).map(
        (layer) => layer.id
      );
      const currentLayerIds = Array.isArray(workspace.layerIds)
        ? workspace.layerIds.filter(
            (layerId): layerId is string => typeof layerId === 'string' && layerId.trim().length > 0
          )
        : [];

      const hasSameMembership =
        currentLayerIds.length === nextLayerIds.length &&
        currentLayerIds.every((layerId, index) => layerId === nextLayerIds[index]);
      if (hasSameMembership) return;
      const projectId = requireWorkspaceProjectIdFromKernel(kernel, workspaceId, 'useLayers');

      k.setWorkspace(workspaceId, (currentWorkspace) => ({
        ...currentWorkspace,
        layerIds: [...nextLayerIds],
      }), projectId);
    });
  }, [kernel]);

  const loadLayer = useCallback((layerInput: unknown) => {
    const layerRecord = (layerInput ?? {}) as Record<string, unknown>;
    const layerId = normalizeLayerIdentifier(layerRecord.id);
    if (!layerId) {
      throw new Error('[useLayers.loadLayer] Missing explicit layer.id.');
    }

    const layerWorkspaceId = Object.prototype.hasOwnProperty.call(layerRecord, 'layerWorkspaceId')
      ? normalizeLayerWorkspaceId(layerRecord.layerWorkspaceId)
      : normalizeLayerWorkspaceId(layerRecord.workspaceId);
    const projectId = normalizeLayerIdentifier(layerRecord.projectId);
    if (!projectId) {
      throw new Error(`[useLayers.loadLayer] Missing explicit projectId for layer "${layerId}".`);
    }

    const rectRecord = (layerRecord.rect ?? null) as Partial<Rect> | null;
    const rectX = toFiniteNumber(rectRecord?.x);
    const rectY = toFiniteNumber(rectRecord?.y);
    const rectW = toFiniteNumber(rectRecord?.w);
    const rectH = toFiniteNumber(rectRecord?.h);
    // A 512 px square was the size of a raster that had not loaded yet; a vector artboard can be one.
    const isLegacyPlaceholderRect =
      layerRecord.type !== 'svg' &&
      isLegacyPlaceholderRectSize(rectW) &&
      isLegacyPlaceholderRectSize(rectH);
    const hasValidRect =
      rectX !== null &&
      rectY !== null &&
      rectW !== null &&
      rectH !== null &&
      rectW > 0 &&
      rectH > 0 &&
      !isLegacyPlaceholderRect;
    const rect: Rect = {
      x: rectX ?? 0,
      y: rectY ?? 0,
      w: rectW !== null && rectW > 0 && !isLegacyPlaceholderRect ? rectW : 0,
      h: rectH !== null && rectH > 0 && !isLegacyPlaceholderRect ? rectH : 0,
    };
    if (!hasValidRect) {
      console.warn('[useLayers.loadLayer] Auto-healed invalid layer rect.', {
        layerId,
        rectInput: layerRecord.rect,
        healedRect: rect,
      });
    }

    const typeValue = layerRecord.type;
    const normalizedType = typeValue === 'video' || typeValue === 'audio' || typeValue === 'svg'
      ? typeValue
      : undefined;

    const renderOrder = toFiniteNumber(layerRecord.renderOrder);
    const opacity = toFiniteNumber(layerRecord.opacity);
    const scale = toFiniteNumber(layerRecord.scale);
    const contentVersion = toFiniteNumber(layerRecord.contentVersion);
    const timestamp = toFiniteNumber(layerRecord.timestamp);

    const topLayerValue = layerRecord['top-layer'] ?? layerRecord.topLayer;
    const params = layerRecord.params as DiffusionParams | undefined;

    const loadedLayer: Layer = {
      ...(layerRecord as Partial<Layer>),
      id: layerId,
      projectId,
      renderOrder: renderOrder ?? undefined,
      'top-layer': topLayerValue === true,
      imageSrc: typeof layerRecord.imageSrc === 'string' ? layerRecord.imageSrc : undefined,
      rect,
      imageData: null,
      visible: layerRecord.visible !== false,
      name:
        (typeof layerRecord.name === 'string' && layerRecord.name.trim().length > 0
          ? layerRecord.name
          : undefined) || `Layer ${layerId}`,
      locked: layerRecord.locked !== false,
      type: normalizedType,
      opacity: opacity ?? 1,
      scale: scale !== null && scale > 0 ? scale : undefined,
      layerWorkspaceId,
      params,
      source: typeof layerRecord.source === 'string' ? layerRecord.source : 'unknown',
      timestamp: timestamp ?? undefined,
      contentVersion: contentVersion ?? undefined,
      videoSrc: typeof layerRecord.videoSrc === 'string' ? layerRecord.videoSrc : undefined,
      videoFramesUrls: Array.isArray(layerRecord.videoFramesUrls)
        ? layerRecord.videoFramesUrls.filter((value): value is string => typeof value === 'string')
        : undefined,
      loadError: layerRecord.loadError === true,
      modificationLog: Array.isArray(layerRecord.modificationLog)
        ? layerRecord.modificationLog as LayerModificationLogEntry[]
        : undefined,
      reconstructedInputUrl:
        typeof layerRecord.reconstructedInputUrl === 'string'
          ? layerRecord.reconstructedInputUrl
          : undefined,
    };

    return {
      layer: loadedLayer,
    };
  }, [normalizeLayerIdentifier, toFiniteNumber]);

  const persistLayerCreate = useCallback((layer: Layer, context: string) => {
    // setLayer owns the serialized create/update queue, including a detach while CREATE is pending; the CREATE carries uploaded pixels.
    void kernel.current?.flushAutosave('StudioLayer', layer.id, STUDIO_LAYER_DTO_AUTOSAVE_SCOPE).catch((error) => {
      console.error(`[${context}] Failed to persist created layer "${layer.id}".`, error);
    });
  }, [kernel]);

  const ensureWorkspaceVersionAfterLayerCreate = useCallback((workspaceId: string, reason: string) => {
    const normalizedWorkspaceId = typeof workspaceId === 'string' ? workspaceId.trim() : '';
    if (!kernel.current || !normalizedWorkspaceId) return;
    const projectId = kernel.current.getWorkspace(normalizedWorkspaceId)?.projectId ?? '';
    const outputScope = resolveGenerationOutputScopeForView(
      projectId,
      normalizedWorkspaceId,
      kernel.current.getStudioViewMode(),
    );
    void kernel.current.ensureWorkspaceVersionAfterCompositionCommit(
      normalizedWorkspaceId,
      reason,
      outputScope ? { outputScope } : undefined,
    ).catch((error) => {
      console.error(`[useLayers] Failed to ensure workspace version for "${normalizedWorkspaceId}".`, error);
    });
  }, [kernel]);

  const emitStructuredStudioMutationEvent = useCallback((options: {
    projectId: string;
    workspaceId?: string | null;
    eventType: string;
    payload: Record<string, unknown>;
  }) => {
    const k = kernel.current;
    if (!k) return;

    const projectId = typeof options.projectId === 'string' ? options.projectId.trim() : '';
    if (!projectId) return;
    const activeSessionId = k.getActiveChatSessionId();
    if (!activeSessionId) return;
    const activeSessionProjectIdRaw = k.getLastProjectId(activeSessionId);
    const activeSessionProjectId =
      typeof activeSessionProjectIdRaw === 'string' ? activeSessionProjectIdRaw.trim() : '';
    if (activeSessionProjectId !== projectId) return;

    const workspaceId = typeof options.workspaceId === 'string' ? options.workspaceId.trim() : '';
    const eventPayload: Record<string, unknown> = {
      ...options.payload,
      projectId,
      workspaceId: workspaceId || null,
      createdAt: new Date().toISOString(),
    };

    void k.mutateInventory([{
      action: 'CREATE',
      entity: 'TimelineEvent',
      tempId: `timeline-event-studio-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
      payload: {
        chatSessionId: activeSessionId,
        targetEntityType: 'Project',
        targetEntityId: projectId,
        category: 'MUTATION',
        eventType: options.eventType,
        payload: eventPayload,
      },
    }] as any, `Create studio mutation event ${options.eventType}`).catch((error) => {
      console.error('[useLayers] Failed to emit structured studio mutation event', {
        eventType: options.eventType,
        projectId,
        workspaceId,
        error,
      });
    });
  }, [kernel]);

  const computeLayerDelta = useCallback((previousLayers: Layer[], nextLayers: Layer[]) => {
    const previousById = new Map(previousLayers.map((layer) => [layer.id, layer]));
    const nextById = new Map(nextLayers.map((layer) => [layer.id, layer]));

    const deletes: string[] = [];
    const upserts: Layer[] = [];

    previousById.forEach((_, layerId) => {
      if (!nextById.has(layerId)) {
        deletes.push(layerId);
      }
    });

    nextById.forEach((nextLayer, layerId) => {
      const previousLayer = previousById.get(layerId);
      if (!previousLayer || previousLayer !== nextLayer) {
        upserts.push(nextLayer);
      }
    });

    return { upserts, deletes };
  }, []);

  const applyLayerDelta = useCallback((delta: { upserts: Layer[]; deletes: string[] }) => {
    if (!kernel.current) return;
    const k = kernel.current;
    const affectedWorkspaceIds = new Set<string>();
    delta.deletes.forEach((layerId) => {
      const existingLayer = k.getLayerRegistry()[layerId];
      const existingWorkspaceId =
        typeof existingLayer?.layerWorkspaceId === 'string'
          ? existingLayer.layerWorkspaceId.trim()
          : '';
      if (existingWorkspaceId) {
        affectedWorkspaceIds.add(existingWorkspaceId);
      }
      k.setLayer(layerId, null);
    });
    delta.upserts.forEach((layer) => {
      const previousLayer = k.getLayerRegistry()[layer.id];
      const previousWorkspaceId =
        typeof previousLayer?.layerWorkspaceId === 'string'
          ? previousLayer.layerWorkspaceId.trim()
          : '';
      const nextWorkspaceId =
        typeof layer.layerWorkspaceId === 'string' ? layer.layerWorkspaceId.trim() : '';
      if (previousWorkspaceId) {
        affectedWorkspaceIds.add(previousWorkspaceId);
      }
      if (nextWorkspaceId) {
        affectedWorkspaceIds.add(nextWorkspaceId);
      }
      k.setLayer(layer.id, layer);
    });
    if (affectedWorkspaceIds.size > 0) {
      syncWorkspaceLayerMembership(Array.from(affectedWorkspaceIds));
    }
  }, [kernel, syncWorkspaceLayerMembership]);


  const commitLayerCorrection = useCallback((
    layer: Layer,
    next: LayerCorrection,
    description: string,
    logTypes: LayerModificationLogEntry['type'][],
  ) => {
    const undoState = snapshotLayerCorrection(layer, next);
    const undoLog = cloneModificationLog(layer.modificationLog);
    kernel.current?.executeAction({
      description,
      redo: () => {
        if (!kernel.current) return;
        const timestamp = new Date().toISOString();
        kernel.current.setLayer(layer.id, (existingLayer) => ({
          ...existingLayer,
          ...next,
          modificationLog: mergeModificationLogs(existingLayer.modificationLog, undoLog, logTypes.map((type) => ({ timestamp, type }))),
        }));
        kernel.current.invalidateWorkspaceVersionCache(layer.layerWorkspaceId);
      },
      undo: () => {
        if (!kernel.current) return;
        kernel.current.setLayer(layer.id, (existingLayer) => ({
          ...existingLayer,
          ...undoState,
          modificationLog: cloneModificationLog(undoLog),
        }));
        kernel.current.invalidateWorkspaceVersionCache(layer.layerWorkspaceId);
      },
    });
  }, [kernel]);

  // The layers beneath are found by id, so a candidate that is not in the store yet is placed where its stored layer stands.
  const resolveUnderlyingReference = useCallback((
    layer: Layer,
    imageData: ImageData,
    workspaceId: string,
    underlyingLayersOverride?: Layer[],
  ) => {
    if (!kernel.current) throw new Error('Kernel not available.');
    const layersInContext = kernel.current.getLayersInWorkspaceContext(workspaceId);
    const layerIndexInContext = layersInContext.findIndex(l => l.id === layer.id);
    const underlyingLayerIds = (underlyingLayersOverride ?? layersInContext.slice(0, layerIndexInContext === -1 ? layersInContext.length : layerIndexInContext))
      .filter(l => l.visible)
      .map(l => l.id);
    if (underlyingLayerIds.length === 0) return null;

    const workspace = kernel.current.getWorkspace(workspaceId);
    if (!workspace) throw new Error('Workspace for layer not found.');

    const layerWorldRect = {
      x: workspace.frameRect.x + layer.rect.x,
      y: workspace.frameRect.y + layer.rect.y,
      w: imageData.width * (layer.scale ?? 1),
      h: imageData.height * (layer.scale ?? 1),
    };
    const transform = resolveStudioLayerTransform(layer.params);
    return { underlyingLayerIds, workspace, layerWorldRect, transform, worldRect: getTransformedRectBounds(layerWorldRect, transform) };
  }, [kernel]);

  const computeAlignment = useCallback(async (
    layerToAlign: Layer,
    { automatic, fromMemory, underlyingLayersOverride }: { automatic: boolean; fromMemory: boolean; underlyingLayersOverride?: Layer[] },
  ): Promise<
    | { status: 'nothing-beneath' }
    | { status: 'covers-frame' }
    | { status: 'out-of-tolerance' }
    | { status: 'refused'; reason: string }
    | { status: 'aligned'; next: LayerCorrection }
  > => {
    if (!kernel.current) {
      throw new Error("Kernel not available for alignment.");
    }

    const cv = await cvReadyPromise;

    if (!layerToAlign || !layerToAlign.imageData) {
      throw new Error("Layer to align not found or has no image data.");
    }
    const workspaceId = layerToAlign.layerWorkspaceId;
    if (!workspaceId) throw new Error('Project layers cannot be aligned in a workspace context.');

    const reference = resolveUnderlyingReference(layerToAlign, layerToAlign.imageData, workspaceId, underlyingLayersOverride);
    if (!reference) {
      console.warn("No visible underlying layers to align to.");
      return { status: 'nothing-beneath' };
    }
    const { underlyingLayerIds, workspace, layerWorldRect, worldRect, transform } = reference;
    // A rotated or mirrored layer's rect is not what it covers: the frame-edge rules read untransformed layers only.
    const untransformed = transform.rotationDeg === 0 && !transform.flipX && !transform.flipY;
    const before = { ...layerWorldRect, x: layerWorldRect.x - workspace.frameRect.x, y: layerWorldRect.y - workspace.frameRect.y };
    if (untransformed && coversWholeFrame(workspace.frameRect, before)) return { status: 'covers-frame' };
    const layerRender = toLayerRenderRequest(layerToAlign, fromMemory);

    // --- OPTIMIZATION: Cap the analysis resolution ---
    // Define a reasonable cap for the analysis image size (e.g., ~2 Megapixels).
    // This prevents us from processing massive 16MP images in the browser.
    const MAX_ALIGNMENT_PIXELS = 1440 * 1440;
    const targetPixelsForAlignment = Math.min(layerToAlign.imageData.width * layerToAlign.imageData.height, MAX_ALIGNMENT_PIXELS);

    const [underlyingResult, layerToAlignResult] = await Promise.all([
      // Render the underlying layers at the capped resolution
      kernel.current.renderWorkspaceArea(
        workspaceId,
        underlyingLayerIds,
        targetPixelsForAlignment,
        false, false, 1, worldRect
      ),
      // Render the layer-to-align as well, ensuring both images are the same size for analysis.
      kernel.current.renderWorkspaceArea(
        workspaceId,
        layerRender.layerIds, // Render only the layer to be aligned
        targetPixelsForAlignment,
        false, false, 1, worldRect,
        undefined, // outputFormat
        undefined, // gridOverlay
        undefined, // maskInterpretation
        layerRender.extraLayers
      )
    ]);

    const underlyingImageData = underlyingResult.imageData;
    const layerToAlignImageData = layerToAlignResult.imageData;

    if (!underlyingImageData || !layerToAlignImageData) {
      throw new Error("Could not render content for alignment.");
    }

    // The auto path aligns a generated layer not yet in the store: patching it would leave an orphan runtime entry.
    const isInStore = () => kernel.current?.getInventory().StudioLayer.registry[layerToAlign.id] !== undefined;
    if (isInStore()) kernel.current.patchEntityRuntimeState('StudioLayer', layerToAlign.id, { isAligning: true });
    const alignStart = now();
    let alignment: Awaited<ReturnType<typeof runLayerAlignmentWithFallback>>;
    try {
      alignment = await runLayerAlignmentWithFallback(
        kernel.current.opencvWorkerRef.current,
        kernel.current.createOpenCvWorker,
        layerToAlignImageData,
        underlyingImageData,
        cv,
      );
    } finally {
      if (isInStore()) kernel.current?.patchEntityRuntimeState('StudioLayer', layerToAlign.id, { isAligning: false });
    }

    if (kernel.current?.getUserPreferences()?.performanceHudEnabled === true) {
      console.log('[Perf][layerAlign] done', {
        layerId: layerToAlign.id,
        size: `${underlyingImageData.width}x${underlyingImageData.height}`,
        executionPath: alignment.executionPath,
        consensus: alignment.result.consensus,
        timingsMs: { total: roundMs(now() - alignStart) },
      });
    }

    // Manual is best effort, automatic keeps the gate: the reasons are in matrixToApply.
    const { refusedFor, consensus } = alignment.result;
    const applied = matrixToApply(alignment.result, !automatic);
    if (!applied) {
      // On the manual path the gate's own reasons never apply, so the only thing left to say is that nothing matched.
      const reason = !automatic && consensus ? 'no part of the layer matched the image underneath' : refusedFor.join('; ');
      console.warn('Alignment refused:', reason);
      return { status: 'refused', reason };
    }

    const [a, b, c, d, e, f] = applied as [number, number, number, number, number, number];

    const upscaleFactor = underlyingResult.upscaleFactor;

    const delta_x = c;
    const delta_y = f;
    const scale_x = Math.sqrt(a * a + b * b);
    const scale_y = Math.sqrt(d * d + e * e);
    const uniformScaleMultiplier = (scale_x + scale_y) / 2;

    if (automatic) { // This is a call from an automated task, apply tolerances
      const dx_world = delta_x / upscaleFactor;
      const dy_world = delta_y / upscaleFactor;
      const w_world = layerToAlign.imageData.width * (layerToAlign.scale ?? 1);
      const h_world = layerToAlign.imageData.height * (layerToAlign.scale ?? 1);

      const MAX_TRANSLATION_PERCENT = 0.05; // 5% of layer diagonal
      const MAX_SCALE_DEVIATION = 0.05; // 5% (e.g., 0.9 to 1.1)

      const translation_dist = Math.sqrt(dx_world * dx_world + dy_world * dy_world);
      const layer_diagonal = Math.sqrt(w_world * w_world + h_world * h_world);

      if (layer_diagonal > 0 && translation_dist > layer_diagonal * MAX_TRANSLATION_PERCENT) {
          console.log(`[Align] Massive translation correction detected (${translation_dist.toFixed(2)}px > ${MAX_TRANSLATION_PERCENT * 100}% of diagonal ${layer_diagonal.toFixed(2)}px). Ignoring alignment for task.`);
          return { status: 'out-of-tolerance' };
      }

      if (Math.abs(uniformScaleMultiplier - 1) > MAX_SCALE_DEVIATION) {
          console.log(`[Align] Massive scaling correction detected (factor: ${uniformScaleMultiplier.toFixed(3)}). Ignoring alignment for task.`);
          return { status: 'out-of-tolerance' };
      }
    }

    const fittedScale = (layerToAlign.scale ?? 1) * uniformScaleMultiplier;
    const fitted = {
      x: layerWorldRect.x + (delta_x / upscaleFactor) - workspace.frameRect.x,
      y: layerWorldRect.y + (delta_y / upscaleFactor) - workspace.frameRect.y,
      w: layerToAlign.imageData.width * fittedScale,
      h: layerToAlign.imageData.height * fittedScale,
    };
    const aligned = untransformed ? keepFrameEdgesCovered(workspace.frameRect, before, fitted) : fitted;

    return {
      status: 'aligned',
      next: {
        rect: { ...layerToAlign.rect, ...aligned },
        imageData: layerToAlign.imageData,
        scale: aligned.w / layerToAlign.imageData.width,
        contentVersion: Date.now(),
      },
    };
  }, [kernel, resolveUnderlyingReference]);

  const alignLayerToUnderlying = useCallback(async (layerToAlign: Layer): Promise<void> => {
    const outcome = await computeAlignment(layerToAlign, { automatic: false, fromMemory: false });
    if (outcome.status === 'refused' || outcome.status === 'covers-frame') {
      kernel.current?.handleError(`Alignment refused: ${outcome.status === 'refused' ? outcome.reason : 'the layer covers the whole frame'}`);
      return;
    }
    if (outcome.status !== 'aligned') return;
    commitLayerCorrection(layerToAlign, outcome.next, `Align Layer: ${layerToAlign.name}`, ['MANUAL_ALIGN']);
  }, [kernel, computeAlignment, commitLayerCorrection]);


  const runLayerRaster = useCallback(async (
    layerId: string,
    sourceImageData: ImageData,
    job: Parameters<typeof computeLayerRaster>[1],
  ) => {
    kernel.current?.patchEntityRuntimeState('StudioLayer', layerId, { isRasterizing: true });
    try {
      const start = now();
      const worker = kernel.current?.blendWorkerRef.current;
      let executionPath: 'worker' | 'worker->fallback-main' | 'main' = 'main';
      let workerError: unknown = null;
      let mainThreadMs = 0;
      let result: ReturnType<typeof computeLayerRaster> | undefined;

      if (worker) {
        const postStart = now();
        const pending = runLayerRasterInWorker(worker, sourceImageData, job);
        mainThreadMs += now() - postStart;
        try {
          result = await pending;
          executionPath = 'worker';
        } catch (error) {
          console.warn('[LayerRaster] Worker raster failed, falling back to main thread.', error);
          workerError = error;
          executionPath = 'worker->fallback-main';
        }
      }
      const workerMs = worker ? now() - start : 0;

      let fallbackMainMs = 0;
      if (result === undefined) {
        const mainStart = now();
        result = computeLayerRaster(sourceImageData, job);
        fallbackMainMs = now() - mainStart;
        mainThreadMs += fallbackMainMs;
      }

      if (job.cut && kernel.current?.getUserPreferences()?.performanceHudEnabled === true) {
        console.log('[Perf][layerCut] done', {
          layerId,
          size: `${sourceImageData.width}x${sourceImageData.height}`,
          polygonCount: job.cut.polygons.length,
          blendRadius: job.blendRadius,
          invert: job.cut.invert,
          executionPath,
          timingsMs: {
            mainThread: roundMs(mainThreadMs),
            total: roundMs(now() - start),
          },
        });
      }

      return { result, executionPath, workerError, workerMs, fallbackMainMs };
    } finally {
      kernel.current?.patchEntityRuntimeState('StudioLayer', layerId, { isRasterizing: false });
    }
  }, [kernel]);

  const computeExtraction = useCallback(async (
    layerToDenoise: Layer,
    fromMemory: boolean,
    underlyingLayersOverride?: Layer[]
  ): Promise<
    | { status: 'nothing-beneath' }
    | { status: 'diff-failed' }
    | { status: 'nothing-changed' }
    | { status: 'extracted'; next: LayerCorrection }
  > => {
    if (!kernel.current) {
      throw new Error("Kernel not available for denoising.");
    }

    if (!layerToDenoise || !layerToDenoise.imageData) {
      throw new Error("Layer to denoise not found or has no image data.");
    }
    const workspaceId = layerToDenoise.layerWorkspaceId;
    if (!workspaceId) throw new Error('Project layers cannot be extracted in a workspace context.');

    const reference = resolveUnderlyingReference(layerToDenoise, layerToDenoise.imageData, workspaceId, underlyingLayersOverride);
    if (!reference) return { status: 'nothing-beneath' };
    const { underlyingLayerIds, workspace, layerWorldRect, transform, worldRect } = reference;
    const layerRender = toLayerRenderRequest(layerToDenoise, fromMemory);

    const targetPixelsForAnalysis = Math.min(layerToDenoise.imageData.width * layerToDenoise.imageData.height, MAX_AUTO_CUT_ANALYSIS_PIXELS);

    const [originalResult, modifiedResult] = await Promise.all([
      kernel.current.renderWorkspaceArea(
        workspaceId,
        underlyingLayerIds,
        targetPixelsForAnalysis,
        false, true, 1, worldRect
      ),
      kernel.current.renderWorkspaceArea(
        workspaceId,
        layerRender.layerIds,
        targetPixelsForAnalysis,
        false, true, 1, worldRect,
        undefined, // outputFormat
        undefined, // gridOverlay
        undefined, // maskInterpretation
        layerRender.extraLayers
      )
    ]);

    const originalImageData = originalResult.imageData;
    const modifiedImageData = modifiedResult.imageData;

    if (!originalImageData || !modifiedImageData) {
      throw new Error("Could not render content for denoising analysis.");
    }

    const analysisUpscaleFactor = originalResult.upscaleFactor ?? 1;
    const isInStore = () => kernel.current?.getInventory().StudioLayer.registry[layerToDenoise.id] !== undefined;
    if (isInStore()) kernel.current.patchEntityRuntimeState('StudioLayer', layerToDenoise.id, { isExtracting: true });
    let extraction: Awaited<ReturnType<typeof runAutoCutExtractionWithFallback>>;
    try {
      extraction = await runAutoCutExtractionWithFallback(
        kernel.current.opencvWorkerRef.current,
        kernel.current.createOpenCvWorker,
        originalImageData,
        modifiedImageData,
        { worldRect, layerWorldRect, transform, analysisUpscaleFactor },
        await cvReadyPromise,
      );
    } finally {
      if (isInStore()) kernel.current?.patchEntityRuntimeState('StudioLayer', layerToDenoise.id, { isExtracting: false });
    }
    if (!extraction.result) return { status: 'diff-failed' };
    const { imageData: localDiffImageData, localRect } = extraction.result;
    if (!localRect) return { status: 'nothing-changed' };

    const nextScale = 1 / analysisUpscaleFactor;
    const nextRect = buildStudioLayerRectPreservingTransformPivot(
      layerToDenoise.rect,
      localRect,
      localDiffImageData.width,
      localDiffImageData.height,
      nextScale,
      layerWorldRect,
      transform,
      workspace.frameRect,
    );
    // Extract always ends with Blend's edge fade, in the same undo step: an extraction left unblended keeps hard crop edges.
    const { result: blendedImageData } = await runLayerRaster(layerToDenoise.id, localDiffImageData, {
      blendRadius: AUTO_CUT_BLEND_RADIUS_PX,
      placement: { rect: nextRect, scale: nextScale, transform, frameRect: workspace.frameRect },
    });
    return {
      status: 'extracted',
      next: {
        imageData: blendedImageData ?? localDiffImageData,
        contentVersion: Date.now(),
        imageSrc: undefined,
        rect: nextRect,
        scale: nextScale,
      },
    };
  }, [kernel, resolveUnderlyingReference, runLayerRaster]);

  const extractChangesFromUnderlying = useCallback(async (
    layerToDenoise: Layer,
    isUndoable: boolean = true,
    underlyingLayersOverride?: Layer[]
  ): Promise<Layer | void> => {
    const outcome = await computeExtraction(layerToDenoise, !isUndoable, underlyingLayersOverride);
    if (outcome.status === 'nothing-beneath') {
      kernel.current?.handleError("No visible underlying layers found to use as a clean reference.");
      return;
    }
    if (outcome.status === 'diff-failed') {
      kernel.current?.handleError("Failed to calculate visual changes between layers.");
      return;
    }
    // Thrown, not toasted: the agent's AUTO_CUT_LAYER learns that nothing was kept, the layer menu toasts the message.
    if (outcome.status === 'nothing-changed') throw new Error(AUTO_CUT_NOTHING_KEPT);
    if (!isUndoable) return applyLayerCorrectionInMemory(layerToDenoise, outcome.next, 'MANUAL_EXTRACT');
    commitLayerCorrection(layerToDenoise, outcome.next, `Auto Cut: ${layerToDenoise.name}`, ['MANUAL_EXTRACT']);
  }, [kernel, computeExtraction, commitLayerCorrection]);



  const harmonizeLayerColor = useCallback(async (
    layerToCorrect: Layer,
    isUndoable: boolean = true,
    harmonizeProfile: 'soft' | 'strict' = 'soft'
  ): Promise<Layer | void> => {
    if (!kernel.current) {
      throw new Error("Kernel not available for color correction.");
    }
    const cv = await cvReadyPromise;

    if (!layerToCorrect || !layerToCorrect.imageData) {
      kernel.current.handleError("Layer to correct not found or has no image data.");
      return;
    }
    const workspaceId = layerToCorrect.layerWorkspaceId;
    if (!workspaceId) throw new Error('Project layers cannot be harmonized in a workspace context.');

    const reference = resolveUnderlyingReference(layerToCorrect, layerToCorrect.imageData, workspaceId);
    if (!reference) {
      kernel.current.handleError("No visible underlying layers found to use as a color reference.");
      return;
    }
    // 2. Render the world-space reference at the transformed layer bounds.
    const { underlyingLayerIds, layerWorldRect, transform, worldRect } = reference;

    const targetPixelsForAnalysis = Math.min(
      layerToCorrect.imageData.width * layerToCorrect.imageData.height,
      MAX_COLOR_HARMONIZATION_ANALYSIS_PIXELS,
    );

    const underlyingResult = await kernel.current.renderWorkspaceArea(
      workspaceId,
      underlyingLayerIds,
      targetPixelsForAnalysis,
      false, true, 1, worldRect
    );

    const underlyingImageData = underlyingResult.imageData;
    if (!underlyingImageData) {
      throw new Error("Could not render content for color correction analysis.");
    }

    const analysisTargetImageData = resizeImageDataToPixelBudget(
      layerToCorrect.imageData,
      MAX_COLOR_HARMONIZATION_ANALYSIS_PIXELS,
    );
    const localReferenceImageData = reprojectStudioLayerWorldImageToLocal(
      underlyingImageData,
      worldRect,
      layerWorldRect,
      transform,
      { x: 0, y: 0, w: layerWorldRect.w, h: layerWorldRect.h },
      analysisTargetImageData.width,
      analysisTargetImageData.height,
    );
    const harmonizedData = await performColorHarmonization(
      localReferenceImageData,
      analysisTargetImageData,
      cv,
      harmonizeProfile,
      layerToCorrect.imageData,
    );

    if (!harmonizedData) {
      throw new Error('Layer Harmonize failed: color correction could not be computed.');
    }
    const next = { imageData: harmonizedData, contentVersion: Date.now(), imageSrc: undefined };
    if (!isUndoable) return applyLayerCorrectionInMemory(layerToCorrect, next, 'MANUAL_HARMONIZE');
    kernel.current.playUISound('SUCCESS', { volume: 0.4 });
    commitLayerCorrection(layerToCorrect, next, `Correct Color: ${layerToCorrect.name}`, ['MANUAL_HARMONIZE']);
  }, [kernel, resolveUnderlyingReference, commitLayerCorrection]);

  // Null when nothing visible lies beneath; throws when the drift cannot be measured. onChange receives the layer's change before and after, refusal included.
  const computeUnchangedHarmonization = useCallback(async (
    layerToCorrect: Layer, underlyingLayersOverride?: Layer[], onChange?: (change: GenerationChange | null) => void,
  ): Promise<LayerCorrection | null> => {
    if (!kernel.current) throw new Error('Kernel not available for harmonization.');
    const layerImageData = layerToCorrect.imageData;
    if (!layerImageData) throw new Error('Layer to harmonize not found or has no image data.');
    const workspaceId = layerToCorrect.layerWorkspaceId;
    if (!workspaceId) throw new Error('Harmonize needs a layer inside a workspace.');

    const reference = resolveUnderlyingReference(layerToCorrect, layerImageData, workspaceId, underlyingLayersOverride);
    if (!reference) return null;
    const { underlyingLayerIds, layerWorldRect, transform, worldRect } = reference;
    const analysis = getColorHarmonizationAnalysisDimensions(layerImageData.width, layerImageData.height);

    const isInStore = () => kernel.current?.getInventory().StudioLayer.registry[layerToCorrect.id] !== undefined;
    if (isInStore()) kernel.current.patchEntityRuntimeState('StudioLayer', layerToCorrect.id, { isHarmonizingUnchanged: true });
    let harmonizedData: ImageData | null;
    try {
      const underlyingResult = await kernel.current.renderWorkspaceArea(
        workspaceId, underlyingLayerIds, analysis.width * analysis.height, false, true, 1, worldRect,
      );
      if (!underlyingResult.imageData) throw new Error('Could not render content for color correction analysis.');
      const { result } = await runDriftHarmonizationWithFallback(
        kernel.current.opencvWorkerRef.current,
        kernel.current.createOpenCvWorker,
        layerImageData,
        {
          underlying: underlyingResult.imageData,
          underlyingWorldRect: worldRect,
          layerWorldRect,
          transform,
          analysisWidth: analysis.width,
          analysisHeight: analysis.height,
        },
        await cvReadyPromise,
      );
      harmonizedData = result.imageData;
      onChange?.(result.change);
    } finally {
      if (isInStore()) kernel.current?.patchEntityRuntimeState('StudioLayer', layerToCorrect.id, { isHarmonizingUnchanged: false });
    }
    if (!harmonizedData) throw new LayerCorrectionRefusal('Too little of this layer is unchanged over the layers beneath it to measure its color drift.');
    return { imageData: harmonizedData, contentVersion: Date.now(), imageSrc: undefined };
  }, [kernel, resolveUnderlyingReference]);

  const harmonizeLayerUnchanged = useCallback(async (layerToCorrect: Layer): Promise<void> => {
    if (!kernel.current) throw new Error('Kernel not available for harmonization.');
    if (!layerToCorrect.imageData) {
      kernel.current.handleError('Layer to harmonize not found or has no image data.');
      return;
    }
    let next: LayerCorrection | null;
    try {
      next = await computeUnchangedHarmonization(layerToCorrect);
    } catch (error) {
      if (!(error instanceof LayerCorrectionRefusal)) throw error;
      toast.warning(error.message);
      return;
    }
    if (!next) {
      kernel.current?.handleError('No visible underlying layers found to use as a color reference.');
      return;
    }
    kernel.current?.playUISound('SUCCESS', { volume: 0.4 });
    commitLayerCorrection(layerToCorrect, next, `Harmonize: ${layerToCorrect.name}`, ['MANUAL_UNCHANGED_HARMONIZE']);
  }, [kernel, computeUnchangedHarmonization, commitLayerCorrection]);

  // Nothing here touches the store or the undo stack: each step works on the previous step's candidate.
  const computeLayerIntegration = useCallback((
    layer: Layer,
    { automatic = false, harmonize = true, underlyingLayersOverride, onChange }: {
      automatic?: boolean; harmonize?: boolean; underlyingLayersOverride?: Layer[]; onChange?: (change: GenerationChange | null) => void;
    } = {},
  ) => runLayerIntegration(layer, {
    align: async (candidate) => {
      const outcome = await computeAlignment(candidate, { automatic, fromMemory: true, underlyingLayersOverride });
      if (outcome.status === 'covers-frame') return ALIGN_SKIPPED;
      return outcome.status === 'aligned' ? { ...candidate, ...outcome.next } : null;
    },
    harmonize: async (candidate) => {
      if (!harmonize) return null;
      const next = await computeUnchangedHarmonization(candidate, underlyingLayersOverride, onChange);
      return next ? { ...candidate, ...next } : null;
    },
  }), [computeAlignment, computeUnchangedHarmonization]);

  /** The share of its area a layer Harmonize did not recolour changes, from one render of what lies beneath at the measure's own small size. */
  const measureGeneratedLayerChange = useCallback(async (layer: Layer, underlyingLayersOverride: Layer[]): Promise<GenerationChange | null> => {
    const imageData = layer.imageData;
    const workspaceId = layer.layerWorkspaceId;
    if (!kernel.current || !imageData || !workspaceId) return null;
    const reference = resolveUnderlyingReference(layer, imageData, workspaceId, underlyingLayersOverride);
    if (!reference) return null;
    const size = layerChangeDimensions(imageData.width, imageData.height);
    const rendered = await kernel.current.renderWorkspaceArea(workspaceId, reference.underlyingLayerIds, size.width * size.height, false, true, 1, reference.worldRect);
    if (!rendered.imageData) return null;
    return measureLayerChange(imageData, {
      underlying: rendered.imageData, underlyingWorldRect: reference.worldRect, layerWorldRect: reference.layerWorldRect, transform: reference.transform,
      analysisWidth: size.width, analysisHeight: size.height,
    });
  }, [kernel, resolveUnderlyingReference]);

  // A generated layer is not in the store yet, so nothing is committed: the caller lands the layer returned, the raw one when Align refused.
  const integrateGeneratedLayer = useCallback<KernelHandle['integrateGeneratedLayer']>(async (layer, underlyingLayersOverride, harmonize) => {
    let change: GenerationChange | null | undefined;
    const outcome = await computeLayerIntegration(layer, { automatic: true, harmonize, underlyingLayersOverride, onChange: (measured) => { change = measured; } });
    const shipped = outcome.status === 'cancelled' ? layer : outcome.layer;
    // Harmonize measured nothing (Align refused, no colour match, or a failure before its measure): the layer as it ships. A failed measure never costs the layer.
    if (change === undefined) {
      change = await measureGeneratedLayerChange(shipped, underlyingLayersOverride).catch((error: unknown) => {
        console.warn('[integrateGeneratedLayer] The change of the generated layer could not be measured.', error);
        return null;
      });
    }
    if (outcome.status === 'cancelled') return { layer, change };
    if (harmonize && outcome.harmonizeSkipped) console.warn('[integrateGeneratedLayer] Harmonize skipped:', outcome.harmonizeError);
    const logTypes = integrationLogTypes(outcome);
    if (logTypes.length === 0) return { layer, change };
    const timestamp = new Date().toISOString();
    return { layer: { ...outcome.layer, modificationLog: mergeModificationLogs(layer.modificationLog, undefined, logTypes.map((type) => ({ timestamp, type }))) }, change };
  }, [computeLayerIntegration, measureGeneratedLayerChange]);

  const integrateLayer = useCallback(async (layer: Layer): Promise<void> => {
    if (!kernel.current) throw new Error('Kernel not available for integration.');
    kernel.current.patchEntityRuntimeState('StudioLayer', layer.id, { isIntegrating: true });
    let outcome: Awaited<ReturnType<typeof computeLayerIntegration>>;
    try {
      outcome = await computeLayerIntegration(layer);
    } finally {
      kernel.current?.patchEntityRuntimeState('StudioLayer', layer.id, { isIntegrating: false });
    }
    if (outcome.status === 'cancelled') {
      toast.warning('This layer could not be aligned to the layers beneath it, so nothing was changed.');
      return;
    }
    if (outcome.harmonizeSkipped) console.warn('[integrateLayer] Harmonize skipped:', outcome.harmonizeError);
    if (outcome.alignSkipped && outcome.harmonizeSkipped) {
      toast.warning('This layer covers the whole frame, so it was not aligned, and its color could not be measured. Nothing was changed.');
      return;
    }
    if (outcome.alignSkipped) toast.warning('This layer covers the whole frame, so it was not aligned. Only Harmonize was applied.');
    else if (outcome.harmonizeSkipped) toast.warning('Harmonize was skipped: the color of this layer could not be measured. Only Align was applied.');
    const { imageData, rect, scale, contentVersion, imageSrc } = outcome.layer;
    commitLayerCorrection(
      layer,
      { imageData, rect, scale, contentVersion, imageSrc },
      `Integrate: ${layer.name}`,
      integrationLogTypes(outcome),
    );
  }, [kernel, computeLayerIntegration, commitLayerCorrection]);



  const getImageLikeTimelineDefaults = useCallback((workspaceId: string, partialLayer: Partial<Layer> = {}): Partial<Layer> | undefined => {
    if (!kernel.current) return undefined;
    if (!isImageLikeLayerType(partialLayer.type)) return undefined;
    if (hasExplicitLayerTimelineTiming(partialLayer)) return undefined;

    const workspace = kernel.current.getWorkspace(workspaceId);
    if (!workspace) return undefined;

    const visibleLayers = getLayersInWorkspaceContext(workspaceId).filter((layer) => layer.visible !== false);
    const hasPlayableMediaTimeline = visibleLayers.some((layer) => {
      if (layer.type !== 'video' && layer.type !== 'audio') return false;
      return typeof layer.videoSrc === 'string' && layer.videoSrc.trim().length > 0;
    });
    const hasInitializedImageTimeline = visibleLayers.some((layer) => (
      isImageLikeLayerType(layer.type) &&
      (toFiniteTimelineMs(layer.timelineStartMs) !== null || toFiniteTimelineMs(layer.timelineEndMs) !== null)
    ));
    const hasActiveTimeline = hasPlayableMediaTimeline
      ? workspace.preferVersionPanelOverTimeline !== true
      : (workspace.imageTimelineManuallyEnabled === true || hasInitializedImageTimeline);
    if (!hasActiveTimeline) return undefined;

    const startMs = Math.max(0, Math.round(toFiniteTimelineMs(workspace.timelineCursorMs) ?? 0));
    return {
      timelineStartMs: startMs,
      timelineEndMs: startMs + DEFAULT_IMAGE_LIKE_TIMELINE_DURATION_MS,
      timelineSourceInMs: 0,
    };
  }, [kernel, getLayersInWorkspaceContext]);


  const addNewLayer = useCallback((workspaceId: string, imageData: ImageData, partialLayer: Partial<Layer> = {}): Layer => {
    if (!kernel.current) {
      throw new Error("Editor context is not available to add a new layer.");
    }

    const normalizedWorkspaceId = workspaceId.trim();
    if (!normalizedWorkspaceId) throw new Error('[addNewLayer] workspaceId is required.');
    const projectId = requireWorkspaceProjectIdFromKernel(kernel, normalizedWorkspaceId, 'useLayers');

    kernel.current.logUserAction('Layer Added', {
      workspaceId: normalizedWorkspaceId,
      layerName: partialLayer.name,
      source: partialLayer.source,
      imageSrc: partialLayer.imageSrc, // Will be present for URL-based layers
      type: partialLayer.type,
    });
    
    let newLayer: Layer;
    const workspaceLayers = getLayersInWorkspaceContext(normalizedWorkspaceId);
    const nextRenderOrder = getNextRenderOrderForWorkspace(workspaceLayers, normalizedWorkspaceId);
    
    const activeWorkspace = kernel.current.getWorkspace(normalizedWorkspaceId);
    if (!activeWorkspace) {
      throw new Error(`[addNewLayer] Active workspace with ID "${workspaceId}" not found.`);
    }
    const currentGenerationRect = activeWorkspace.generationRect;
    const currentFrameRect = activeWorkspace.frameRect;
    const imageLikeTimelineDefaults = getImageLikeTimelineDefaults(workspaceId, partialLayer);

    const hasExplicitLayerPlacement = Boolean(partialLayer.rect);
    const shouldPreserveWorkspaceFrameForLayer = partialLayer.source === 'drawing' || hasExplicitLayerPlacement;
    const isFirstLayerInWorkspace = workspaceLayers.length === 0 && !shouldPreserveWorkspaceFrameForLayer;

    let shouldZoomOnCanvas = false;

    if (isFirstLayerInWorkspace) {
      // This is the first layer in this workspace.
      // We will resize the workspace to match the layer's aspect ratio,
      // while normalizing its total area to 1 megapixel.
      const { width: imgWidth, height: imgHeight } = imageData;

      if (imgWidth <= 0 || imgHeight <= 0) {
        // Fallback for invalid image data, though unlikely.
        // We'll just add it centered in the current generation rect.
        const scale = Math.min(currentGenerationRect.w / 1, currentGenerationRect.h / 1);
        const layerInitialX = currentGenerationRect.x + (currentGenerationRect.w - 1 * scale) / 2;
        const layerInitialY = currentGenerationRect.y + (currentGenerationRect.h - 1 * scale) / 2;
        const defaultLayer: Layer = {
          id: createLayerId(), projectId, timestamp: Date.now(), imageData: imageData,
          rect: { x: layerInitialX - currentFrameRect.x, y: layerInitialY - currentFrameRect.y, w: 0, h: 0 },
          scale, visible: true, name: `Layer ${Date.now()}`, locked: true, opacity: 1,
          layerWorkspaceId: workspaceId, source: 'unknown', contentVersion: Date.now(), renderOrder: nextRenderOrder,
        };
        newLayer = { ...defaultLayer, ...(imageLikeTimelineDefaults ?? {}), ...partialLayer };
      } else {
        const { width: newFrameWidth, height: newFrameHeight } = firstLayerFrameSize(imgWidth, imgHeight);

        // Position the new frame where the old one was.
        const newFrameX = currentFrameRect.x;
        const newFrameY = currentFrameRect.y;

        const newFrameRect = { x: newFrameX, y: newFrameY, w: newFrameWidth, h: newFrameHeight };
        const newGenerationRect = { ...newFrameRect }; // Generation rect matches frame

        const projectId = requireWorkspaceProjectIdFromKernel(kernel, workspaceId, 'useLayers');
        if (!projectId) {
          throw new Error(`[addNewLayer] Missing explicit project scope for workspace "${workspaceId}".`);
        }
        kernel.current.setWorkspace(workspaceId, (workspace) => ({
          ...workspace,
          frameRect: newFrameRect,
        }), projectId);

        kernel.current.setGenerationArea(workspaceId, newGenerationRect.x, newGenerationRect.y, newGenerationRect.w, newGenerationRect.h);

        // Calculate the layer's scale to fit this new standardized frame.
        const scale = newFrameWidth / imgWidth;

        const defaultLayer: Layer = {
          id: createLayerId(),
          projectId,
          timestamp: Date.now(),
          imageData: imageData,
          rect: {
            x: 0, // Positioned at the top-left of the frame (relative coords)
            y: 0,
            w: 0,
            h: 0,
          },
          scale,
          visible: true,
          name: `Layer ${Date.now()}`,
          locked: true,
          opacity: 1,
          layerWorkspaceId: workspaceId,
          source: 'unknown',
          contentVersion: Date.now(),
          renderOrder: nextRenderOrder,
        };
        newLayer = { ...defaultLayer, ...(imageLikeTimelineDefaults ?? {}), ...partialLayer };
        shouldZoomOnCanvas = true;
      }
    } else {
      // Case 3: Subsequent layer in a non-empty workspace.
      // The layer is scaled and centered within the existing generationRect.
      const scale = Math.min(currentGenerationRect.w / imageData.width, currentGenerationRect.h / imageData.height);
      const layerInitialX = currentGenerationRect.x + (currentGenerationRect.w - imageData.width * scale) / 2;
      const layerInitialY = currentGenerationRect.y + (currentGenerationRect.h - imageData.height * scale) / 2;

      const defaultLayer: Layer = {
        id: createLayerId(),
        projectId,
        timestamp: Date.now(),
        imageData: imageData,
        rect: {
          x: layerInitialX - currentFrameRect.x,
          y: layerInitialY - currentFrameRect.y,
          w: 0,
          h: 0,
        },
        scale,
        visible: true,
        name: `Layer ${Date.now()}`,
        locked: true,
        opacity: 1,
        layerWorkspaceId: workspaceId,
        source: 'unknown',
        contentVersion: Date.now(),
        renderOrder: nextRenderOrder,
      };
      newLayer = { ...defaultLayer, ...(imageLikeTimelineDefaults ?? {}), ...partialLayer };
    }

    // @ts-expect-error - x and y might be passed from older functions not yet updated
    if (newLayer.x !== undefined && newLayer.y !== undefined) {
      const frame = newLayer.layerWorkspaceId === workspaceId
        ? currentFrameRect
        : kernel.current.getWorkspace(newLayer.layerWorkspaceId)?.frameRect;
      if (frame) {
          const previousW = newLayer.rect.w;
          const previousH = newLayer.rect.h;
          newLayer.rect = {
              // @ts-expect-error Legacy absolute x support
              x: newLayer.x - frame.x,
              // @ts-expect-error Legacy absolute y support
              y: newLayer.y - frame.y,
              w: previousW,
              h: previousH
          };
          // @ts-expect-error Legacy absolute x support
          delete newLayer.x;
          // @ts-expect-error Legacy absolute y support
          delete newLayer.y;
      }
    }

    newLayer = syncRasterLayerRectSize({
      ...newLayer,
      projectId,
      layerWorkspaceId: normalizedWorkspaceId,
    });
    kernel.current.setLayer(newLayer.id, newLayer);
    attachLayerToWorkspace(workspaceId, newLayer.id);
    persistLayerCreate(newLayer, 'useLayers.addNewLayer');
    ensureWorkspaceVersionAfterLayerCreate(workspaceId, 'Imported Layer');
    if (shouldZoomOnCanvas) {
      kernel.current?.zoomOnCanvas(workspaceId);
    }

    return newLayer;
  }, [kernel, getLayersInWorkspaceContext, attachLayerToWorkspace, persistLayerCreate, getImageLikeTimelineDefaults, ensureWorkspaceVersionAfterLayerCreate]);


  const addSvgLayer = useCallback((
    workspaceId: string,
    intrinsicSize: { width: number; height: number },
    partialLayer: Partial<Layer> = {}
  ): Layer => {
    if (!kernel.current) {
      throw new Error("Editor context is not available to add a new SVG layer.");
    }

    const normalizedWorkspaceId = workspaceId.trim();
    if (!normalizedWorkspaceId) throw new Error('[addSvgLayer] workspaceId is required.');
    const projectId = requireWorkspaceProjectIdFromKernel(kernel, normalizedWorkspaceId, 'useLayers');

    kernel.current.logUserAction('SVG Layer Added', {
      workspaceId: normalizedWorkspaceId,
      layerName: partialLayer.name,
      source: partialLayer.source,
      imageSrc: partialLayer.imageSrc,
    });

    const activeWorkspace = kernel.current.getWorkspace(workspaceId);
    if (!activeWorkspace) {
      throw new Error(`[addSvgLayer] Active workspace with ID "${workspaceId}" not found.`);
    }

    const width = Math.max(1, Math.round(intrinsicSize.width));
    const height = Math.max(1, Math.round(intrinsicSize.height));
    const workspaceLayers = getLayersInWorkspaceContext(workspaceId);
    const nextRenderOrder = getNextRenderOrderForWorkspace(workspaceLayers, workspaceId);
    const currentGenerationRect = activeWorkspace.generationRect;
    const currentFrameRect = activeWorkspace.frameRect;
    const imageLikeTimelineDefaults = getImageLikeTimelineDefaults(workspaceId, partialLayer);
    const hasExplicitLayerPlacement = Boolean(partialLayer.rect);
    const shouldPreserveWorkspaceFrameForLayer = partialLayer.source === 'drawing' || hasExplicitLayerPlacement;
    const isFirstLayerInWorkspace = workspaceLayers.length === 0 && !shouldPreserveWorkspaceFrameForLayer;
    let rect: Rect;
    let scale = typeof partialLayer.scale === 'number' && Number.isFinite(partialLayer.scale) && partialLayer.scale > 0
      ? partialLayer.scale
      : 1;
    let shouldZoomOnCanvas = false;

    if (partialLayer.rect) {
      rect = {
        x: Number.isFinite(partialLayer.rect.x) ? partialLayer.rect.x : 0,
        y: Number.isFinite(partialLayer.rect.y) ? partialLayer.rect.y : 0,
        w: Math.max(1, Number.isFinite(partialLayer.rect.w) ? partialLayer.rect.w : width),
        h: Math.max(1, Number.isFinite(partialLayer.rect.h) ? partialLayer.rect.h : height),
      };
      scale = rect.w / width;
    } else if (isFirstLayerInWorkspace) {
      const { width: newFrameWidth, height: newFrameHeight } = firstLayerFrameSize(width, height);
      const newFrameRect = {
        x: currentFrameRect.x,
        y: currentFrameRect.y,
        w: newFrameWidth,
        h: newFrameHeight,
      };
      const projectId = requireWorkspaceProjectIdFromKernel(kernel, workspaceId, 'useLayers');
      kernel.current.setWorkspace(workspaceId, (workspace) => ({
        ...workspace,
        frameRect: newFrameRect,
      }), projectId);
      kernel.current.setGenerationArea(workspaceId, newFrameRect.x, newFrameRect.y, newFrameRect.w, newFrameRect.h);
      rect = { x: 0, y: 0, w: newFrameWidth, h: newFrameHeight };
      scale = newFrameWidth / width;
      shouldZoomOnCanvas = true;
    } else {
      scale = Math.min(currentGenerationRect.w / width, currentGenerationRect.h / height);
      const layerInitialX = currentGenerationRect.x + (currentGenerationRect.w - width * scale) / 2;
      const layerInitialY = currentGenerationRect.y + (currentGenerationRect.h - height * scale) / 2;
      rect = {
        x: layerInitialX - currentFrameRect.x,
        y: layerInitialY - currentFrameRect.y,
        w: width * scale,
        h: height * scale,
      };
    }

    const defaultLayer: Layer = {
      id: createLayerId(),
      projectId,
      timestamp: Date.now(),
      imageData: null,
      rect,
      scale,
      visible: true,
      name: `SVG ${Date.now()}`,
      locked: true,
      opacity: 1,
      layerWorkspaceId: normalizedWorkspaceId,
      source: 'svg',
      type: 'svg',
      contentVersion: Date.now(),
      renderOrder: nextRenderOrder,
    };
    const newLayer: Layer = {
      ...defaultLayer,
      ...(imageLikeTimelineDefaults ?? {}),
      ...partialLayer,
      projectId,
      layerWorkspaceId: normalizedWorkspaceId,
      type: 'svg',
      imageData: null,
      rect,
      scale,
    };

    kernel.current.setLayer(newLayer.id, newLayer);
    attachLayerToWorkspace(workspaceId, newLayer.id);
    persistLayerCreate(newLayer, 'useLayers.addSvgLayer');
    ensureWorkspaceVersionAfterLayerCreate(workspaceId, 'Imported SVG Layer');
    kernel.current.invalidateWorkspaceVersionCache(workspaceId);
    if (shouldZoomOnCanvas) {
      kernel.current.zoomOnCanvas(workspaceId);
    }
    return newLayer;
  }, [kernel, getLayersInWorkspaceContext, attachLayerToWorkspace, persistLayerCreate, getImageLikeTimelineDefaults, ensureWorkspaceVersionAfterLayerCreate]);

  const getImportedImageTimelineDefaults = useCallback((
    workspaceId: string,
    partialLayer: Partial<Layer> = {}
  ): Partial<Layer> | undefined => (
    getImageLikeTimelineDefaults(workspaceId, partialLayer)
  ), [getImageLikeTimelineDefaults]);


  const addLayerFromFile = useCallback(async (workspaceId: string, file: File, partialLayer?: Partial<Layer>): Promise<Layer> => {
    if (!kernel.current) throw new Error("Editor context not available.");
    const newLayerId = createLayerId();
    const defaultPulseToken =
      typeof partialLayer?.pulseToken === 'number' && Number.isFinite(partialLayer.pulseToken)
        ? partialLayer.pulseToken
        : Date.now();

    const isSvgFile = file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg');
    if (isSvgFile) {
      try {
        const svgText = await file.text();
        const intrinsic = parseSvgIntrinsicSize(svgText);
        const projectId = requireWorkspaceProjectIdFromKernel(kernel, workspaceId, 'useLayers');
        const projectOwnerId = kernel.current.getInventory().Project.registry[projectId]?.userId;
        const svgUrl = await uploadFileToCloud(file, file.name, 'image/svg+xml', undefined, projectOwnerId);
        if (!svgUrl) {
          throw new Error("Failed to upload SVG file.");
        }
        kernel.current.registerCloudAssets([svgUrl], 'upload');
        const createdLayer = addSvgLayer(workspaceId, intrinsic, {
          id: newLayerId,
          ...partialLayer,
          name: file.name,
          imageSrc: svgUrl,
          source: 'file',
          pulseToken: defaultPulseToken,
          params: {
            ...(partialLayer?.params && typeof partialLayer.params === 'object' ? partialLayer.params : {}),
            [SVG_LAYER_PARAM_KEY]: {
              svg: svgText,
              url: svgUrl,
              width: intrinsic.width,
              height: intrinsic.height,
              viewBox: intrinsic.viewBox,
              source: 'upload',
            },
          },
          type: 'svg',
          imageData: null,
        });
        kernel.current.invalidateWorkspaceVersionCache(workspaceId);
        return createdLayer;
      } catch (error) {
        console.error("Error handling SVG file:", error);
        throw error;
      }
    }

    const promise = new Promise<Layer>((resolve, reject) => {
      kernel.current?.pendingLayerPromisesRef.current.set(newLayerId, { resolve, reject });

      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;
        img.onload = () => {
          void (async () => {
            try {
              const tempCanvas = document.createElement('canvas');
              tempCanvas.width = img.naturalWidth;
              tempCanvas.height = img.naturalHeight;
              const tempCtx = tempCanvas.getContext('2d');
              if (!tempCtx) {
                throw new Error("Could not get canvas context");
              }
              tempCtx.drawImage(img, 0, 0);
              const decodedImageData = tempCtx.getImageData(0, 0, img.naturalWidth, img.naturalHeight);

              const importedTimelineDefaults = getImportedImageTimelineDefaults(workspaceId, partialLayer);
              const createdLayer = addNewLayer(workspaceId, decodedImageData, {
                id: newLayerId,
                name: file.name,
                source: 'file',
                pulseToken: defaultPulseToken,
                ...(importedTimelineDefaults ?? {}),
                ...partialLayer
              });
              kernel.current?.invalidateWorkspaceVersionCache(workspaceId);
              kernel.current?.pendingLayerPromisesRef.current.get(newLayerId)?.resolve(createdLayer);
              kernel.current?.pendingLayerPromisesRef.current.delete(newLayerId);
            } catch (error) {
              kernel.current?.pendingLayerPromisesRef.current.get(newLayerId)?.reject(error);
              kernel.current?.pendingLayerPromisesRef.current.delete(newLayerId);
            }
          })();
        };
        img.onerror = (error) => {
          console.error("Error loading image:", error);
          kernel.current?.pendingLayerPromisesRef.current.get(newLayerId)?.reject(error);
          kernel.current?.pendingLayerPromisesRef.current.delete(newLayerId);
        };
      };
      reader.onerror = (error) => {
        console.error("Error reading file:", error);
        kernel.current?.pendingLayerPromisesRef.current.get(newLayerId)?.reject(error);
        kernel.current?.pendingLayerPromisesRef.current.delete(newLayerId);
      };
      reader.readAsDataURL(file);
    });

    promise.then(resolvedLayer => {
      kernel.current?.invalidateWorkspaceVersionCache(resolvedLayer.layerWorkspaceId);
    });

    return promise;
  }, [kernel, addNewLayer, addSvgLayer, getImportedImageTimelineDefaults]);


  const addLayerFromUrl = useCallback(async (workspaceId: string, url: string, partialLayer?: Partial<Layer>): Promise<Layer> => {
    if (!kernel.current) throw new Error("Editor context not available.");
    const newLayerId = createLayerId();
    const defaultPulseToken =
      typeof partialLayer?.pulseToken === 'number' && Number.isFinite(partialLayer.pulseToken)
        ? partialLayer.pulseToken
        : Date.now();
    const normalizedSourceUrl = typeof url === 'string' ? url.trim() : '';
    const lowerSourceUrl = normalizedSourceUrl.toLowerCase();
    const normalizedLayerType = partialLayer?.type;

    const isSvgUrl =
      normalizedLayerType === 'svg' ||
      lowerSourceUrl.endsWith('.svg') ||
      lowerSourceUrl.includes('.svg?');
    if (isSvgUrl) {
      const intrinsic = await loadSvgIntrinsicSizeFromUrl(normalizedSourceUrl || url);
      const newLayer = addSvgLayer(workspaceId, intrinsic, {
        id: newLayerId,
        ...partialLayer,
        imageSrc: normalizedSourceUrl || url,
        name: partialLayer?.name ?? `SVG ${newLayerId}`,
        source: partialLayer?.source ?? 'url',
        pulseToken: defaultPulseToken,
        params: {
          ...(partialLayer?.params && typeof partialLayer.params === 'object' ? partialLayer.params : {}),
          [SVG_LAYER_PARAM_KEY]: {
            url: normalizedSourceUrl || url,
            width: intrinsic.width,
            height: intrinsic.height,
            viewBox: intrinsic.viewBox,
            source: partialLayer?.source === 'vectorized' ? 'vectorized' : 'upload',
          },
        },
        type: 'svg',
        imageData: null,
      });
      kernel.current.invalidateWorkspaceVersionCache(workspaceId);
      return newLayer;
    }

    const promise = new Promise<Layer>((resolve, reject) => {
        kernel.current?.pendingLayerPromisesRef.current.set(newLayerId, { resolve, reject });

        const isAudio =
          normalizedLayerType === 'audio' ||
          lowerSourceUrl.endsWith('.mp3') ||
          lowerSourceUrl.endsWith('.wav') ||
          lowerSourceUrl.endsWith('.m4a') ||
          lowerSourceUrl.endsWith('.aac') ||
          lowerSourceUrl.endsWith('.ogg') ||
          lowerSourceUrl.endsWith('.oga') ||
          lowerSourceUrl.endsWith('.flac') ||
          lowerSourceUrl.endsWith('.opus') ||
          lowerSourceUrl.endsWith('.weba');
        const isVideo =
          normalizedLayerType === 'video' ||
          (!isAudio && (
            lowerSourceUrl.endsWith('.mp4') ||
            lowerSourceUrl.endsWith('.mov') ||
            lowerSourceUrl.endsWith('.webm') ||
            lowerSourceUrl.endsWith('.m4v') ||
            lowerSourceUrl.endsWith('.mkv')
          ));

        if (isAudio) {
            try {
                const placeholderImageData = new ImageData(1, 1);
                const createdLayer = addNewLayer(workspaceId, placeholderImageData, {
                    id: newLayerId,
                    videoSrc: normalizedSourceUrl || url,
                    videoFramesUrls: [],
                    name: `Audio ${newLayerId}`,
                    type: 'audio',
                    imageData: null,
                    imageSrc: undefined,
                    pulseToken: defaultPulseToken,
                    ...partialLayer
                });
                kernel.current?.invalidateWorkspaceVersionCache(workspaceId);
                kernel.current?.pendingLayerPromisesRef.current.get(newLayerId)?.resolve(createdLayer);
                kernel.current?.pendingLayerPromisesRef.current.delete(newLayerId);
            } catch (error) {
                kernel.current?.pendingLayerPromisesRef.current.get(newLayerId)?.reject(error);
                kernel.current?.pendingLayerPromisesRef.current.delete(newLayerId);
            }
            return;
        }

        if (isVideo) {
           extractFramesFromVideo(normalizedSourceUrl || url)
                .then(async (result) => {
                    if (!result) {
                        throw new Error("Failed to process video metadata and frames.");
                    }

                    const { frames, width, height } = result;
                    
                    let uploadedFrameUrls: string[] = [];
                    if (frames.length > 0) {
                        try {
                            const projectId = requireWorkspaceProjectIdFromKernel(kernel, workspaceId, 'useLayers');
                            const projectOwnerId = kernel.current?.getInventory().Project.registry[projectId]?.userId;
                            const uploadPromises = frames.map(frameData =>
                                uploadFileToCloud(frameData.frameBlob, `${Date.now()}-frame-${frameData.timestamp.toFixed(2)}.webp`, 'image/webp', undefined, projectOwnerId)
                            );
                            const results = await Promise.all(uploadPromises);
                            uploadedFrameUrls = results.filter((url): url is string => url !== null);
                            if (uploadedFrameUrls.length > 0) {
                              kernel.current?.registerCloudAssets(uploadedFrameUrls, 'upload');
                            }
                        } catch (uploadError) {
                            console.error("Could not upload one or more video frames:", uploadError);
                        }
                    }

                    const placeholderImageData = new ImageData(width, height);

                    const createdLayer = addNewLayer(workspaceId, placeholderImageData, {
                        id: newLayerId,
                        videoSrc: normalizedSourceUrl || url,
                        videoFramesUrls: uploadedFrameUrls,
                        name: `Video ${newLayerId}`,
                        type: 'video',
                        pulseToken: defaultPulseToken,
                        ...partialLayer
                    });
                    kernel.current?.invalidateWorkspaceVersionCache(workspaceId);
                    kernel.current?.pendingLayerPromisesRef.current.get(newLayerId)?.resolve(createdLayer);
                    kernel.current?.pendingLayerPromisesRef.current.delete(newLayerId);
                })
                .catch(error => {
                    console.error(error.message);
                    kernel.current?.pendingLayerPromisesRef.current.get(newLayerId)?.reject(error);
                    kernel.current?.pendingLayerPromisesRef.current.delete(newLayerId);
                });
        } else {
            // Handle Image Layer Creation (existing logic)
            const img = new Image();
            img.crossOrigin = 'anonymous';

            img.onload = () => {
                void (async () => {
                    try {
                        const tempCanvas = document.createElement('canvas');
                        tempCanvas.width = img.naturalWidth;
                        tempCanvas.height = img.naturalHeight;
                        const tempCtx = tempCanvas.getContext('2d');
                        if (!tempCtx) {
                            throw new Error("Could not get canvas context");
                        }
                        tempCtx.drawImage(img, 0, 0);
                        const decodedImageData = tempCtx.getImageData(0, 0, img.naturalWidth, img.naturalHeight);

                        const importedTimelineDefaults = getImportedImageTimelineDefaults(workspaceId, partialLayer);
                        const createdLayer = addNewLayer(workspaceId, decodedImageData, {
                            id: newLayerId,
                            imageSrc: url,
                            name: `Layer ${newLayerId}`,
                            source: 'url',
                            pulseToken: defaultPulseToken,
                            ...(importedTimelineDefaults ?? {}),
                            ...partialLayer
                        });
                        kernel.current?.invalidateWorkspaceVersionCache(workspaceId);
                        kernel.current?.pendingLayerPromisesRef.current.get(newLayerId)?.resolve(createdLayer);
                        kernel.current?.pendingLayerPromisesRef.current.delete(newLayerId);
                    } catch (error) {
                        kernel.current?.pendingLayerPromisesRef.current.get(newLayerId)?.reject(error);
                        kernel.current?.pendingLayerPromisesRef.current.delete(newLayerId);
                    }
                })();
            };

            img.onerror = () => {
                const error = new Error(`Error loading image from URL: ${url}`);
                console.error(error.message);
                kernel.current?.pendingLayerPromisesRef.current.get(newLayerId)?.reject(error);
                kernel.current?.pendingLayerPromisesRef.current.delete(newLayerId);
            };

            img.src = url;
        }
    });

    promise.then(resolvedLayer => {
      kernel.current?.invalidateWorkspaceVersionCache(resolvedLayer.layerWorkspaceId);
    });

    return promise;
  }, [kernel, addNewLayer, addSvgLayer, getImportedImageTimelineDefaults]);


  const addLayerFromImageData = useCallback((workspaceId: string | null | undefined, imageData: ImageData, partialLayer?: Partial<Layer>) => {
    if (!workspaceId) throw new Error('[addLayerFromImageData] workspaceId is required.');
    const newLayer = addNewLayer(workspaceId, imageData, {
      source: 'imagedata',
      ...partialLayer
    });
    return newLayer;
  }, [addNewLayer]);

  const updateLayerVisibility = useCallback((layerId: string) => {
    if (!kernel.current) return;
    const targetLayer = getLayerById(layerId);
    if (!targetLayer || !targetLayer.layerWorkspaceId) return;
    const workspaceId = targetLayer.layerWorkspaceId;
    const projectId = requireWorkspaceProjectIdFromKernel(kernel, workspaceId, 'useLayers');
    const shouldHydrateAfterToggle = !targetLayer.visible;
    const nextVisible = !targetLayer.visible;
    kernel.current?.logUserAction('Layer Visibility Toggled', {
      layer_id: layerId,
      layer_name: targetLayer.name,
      new_state: nextVisible
    });

    kernel.current.setLayer(layerId, { ...targetLayer, visible: nextVisible });
    const matchedVersionId = kernel.current.syncActiveWorkspaceVersionFromLayerVisibility(workspaceId);
    if (shouldHydrateAfterToggle && projectId) {
      kernel.current.hydrateVisibleLayers(projectId);
    }
    if (!matchedVersionId && kernel.current.ensureCurrentStateAsVersion) {
      void kernel.current.ensureCurrentStateAsVersion(
        workspaceId,
        'Layer Visibility Toggle',
        { skipBuriedLayerCleanup: true },
      );
    }
  }, [kernel, getLayerById]);

  const hideBuriedLayers = useCallback(async (workspaceId: string): Promise<string[]> => {
    if (!kernel.current) return [];
    const buriedIds = await findBuriedLayerIdsOfStack(kernel.current.buriedLayersWorkerRef, () => {
      const workspace = kernel.current?.getWorkspace(workspaceId);
      if (!kernel.current || !workspace) return null;
      return describeBuriedLayerStack(workspace, kernel.current.getLayersInWorkspaceContext(workspaceId));
    });
    if (!kernel.current || buriedIds.length === 0) return [];

    const projectId = requireWorkspaceProjectIdFromKernel(kernel, workspaceId, 'useLayers');
    const setVisible = (visible: boolean) => {
      buriedIds.forEach((layerId) => {
        const layer = kernel.current?.getLayerRegistry()[layerId];
        if (layer && layer.visible !== visible) kernel.current?.setLayer(layerId, { ...layer, visible });
      });
      kernel.current?.syncActiveWorkspaceVersionFromLayerVisibility(workspaceId);
    };
    kernel.current.executeAction({
      description: 'Hide buried layers',
      redo: () => setVisible(false),
      undo: () => {
        setVisible(true);
        if (projectId) kernel.current?.hydrateVisibleLayers(projectId);
      },
    });
    kernel.current.logUserAction('Buried Layers Hidden', { workspace_id: workspaceId, hidden_count: buriedIds.length });
    return buriedIds;
  }, [kernel]);

  const updateLayerLock = useCallback((layerId: string) => {
    if (!kernel.current) return;
    kernel.current.setLayer(layerId, (currentLayer) => {
      kernel.current?.logUserAction('Layer Lock Toggled', { layer_id: layerId, layer_name: currentLayer.name, new_state: !currentLayer.locked });
      return { ...currentLayer, locked: !currentLayer.locked };
    });
  }, [kernel]);


  const removeLayer = useCallback((layerId: string) => {
    if (!kernel.current) return;
    const k = kernel.current;
    const targetLayer = k.getLayerRegistry()[layerId];
    if (!targetLayer) {
      console.warn(`[removeLayer] Layer with ID ${layerId} not found.`);
      return;
    }

    const workspaceId = normalizeLayerIdentifier(targetLayer.layerWorkspaceId);
    if (!workspaceId) {
      kernel.current?.handleError('Project layers cannot be removed from a workspace context.');
      return;
    }

    const workspace = k.getWorkspace(workspaceId);
    if (!workspace) {
      console.warn(`[removeLayer] Workspace with ID ${workspaceId} not found for layer ${layerId}.`);
      return;
    }

    const projectId = requireWorkspaceProjectIdFromKernel(kernel, workspaceId, 'useLayers');
    const selectedIdsBefore = projectId ? getProjectSelectedLayersId(k, projectId) : [];
    const selectedIdsAfter = selectedIdsBefore.filter((id) => id !== layerId);
    const workspaceLayerIdsBefore = [...workspace.layerIds];
    const workspaceVersionsBefore = (workspace.versions ?? []).map((version) => ({
      ...version,
      layerVisibility: Array.isArray(version.layerVisibility)
        ? version.layerVisibility.map((entry) => ({ ...entry }))
        : [],
    }));
    const workspaceActiveVersionIdBefore = workspace.activeVersionId ?? null;
    const workspaceValidatedVersionIdBefore = workspace.validatedVersionId ?? null;

    k.executeAction({
      description: `Detach Layer: ${targetLayer.name || `Layer ${targetLayer.id}`}`,
      redo: () => {
        if (!kernel.current) return;
        kernel.current.setNativeLayerCutSession((previous) => {
          if (!previous || previous.layerId !== layerId) return previous;
          return null;
        });
        kernel.current.logUserAction('Layer Detached From Workspace', {
          layer_id: layerId,
          layer_name: targetLayer.name,
          workspace_id: workspaceId,
        });
        kernel.current.detachLayerFromWorkspace(workspaceId, layerId);
        setProjectSelectedLayersId(kernel.current, projectId, selectedIdsAfter);
      },
      undo: () => {
        if (!kernel.current) return;
        kernel.current.logUserAction('Layer Reattached To Workspace', {
          layer_id: layerId,
          layer_name: targetLayer.name,
          workspace_id: workspaceId,
        });
        kernel.current.attachLayerToWorkspace(workspaceId, layerId);
        kernel.current.setWorkspace(workspaceId, (currentWorkspace) => ({
          ...currentWorkspace,
          layerIds: [...workspaceLayerIdsBefore],
          versions: workspaceVersionsBefore.map((version) => ({
            ...version,
            layerVisibility: Array.isArray(version.layerVisibility)
              ? version.layerVisibility.map((entry) => ({ ...entry }))
              : [],
          })),
          activeVersionId: workspaceActiveVersionIdBefore,
          validatedVersionId: workspaceValidatedVersionIdBefore,
        }), projectId);
        kernel.current.setLayer(layerId, targetLayer);
        setProjectSelectedLayersId(kernel.current, projectId, selectedIdsBefore);
        kernel.current.invalidateWorkspaceVersionCache(workspaceId);
      },
    });
  }, [kernel, attachLayerToWorkspace, detachLayerFromWorkspace]);

  const reloadLayerAsset = useCallback((layerId: string) => {
    if (!kernel.current) return;
    const kernelHandle = kernel.current;
    const layerToReload = getLayerById(layerId);
    const workspaceId = layerToReload?.layerWorkspaceId?.trim() || '';
    if (!workspaceId) {
      throw new Error(`[useLayers.reloadLayerAsset] Layer "${layerId}" has no explicit workspace scope.`);
    }
    const projectId = requireWorkspaceProjectIdFromKernel(kernel, workspaceId, 'useLayers');
    if (layerToReload) {
      kernelHandle.logUserAction('Layer Asset Reloaded', {
        layer_id: layerId,
        layer_name: layerToReload.name,
      });
    }
    const isVideo = layerToReload?.type === 'video';
    const isSvg = layerToReload?.type === 'svg';
    const source = isVideo ? layerToReload?.videoSrc : layerToReload?.imageSrc;
    if (!source) {
      return;
    }

    // Manual reload is an explicit force action: clear cached runtime payload first.
    kernelHandle.setLayer(layerId, (currentLayer) => ({
      ...currentLayer,
      imageData: null,
      loadError: false,
      mustRender: false,
    }));

    kernelHandle.setInflightLayerIds((previous) => {
      if (!previous.has(layerId)) return previous;
      const next = new Set(previous);
      next.delete(layerId);
      return next;
    });

    kernelHandle.setInflightLayerIds((previous) => {
      if (previous.has(layerId)) return previous;
      const next = new Set(previous);
      next.add(layerId);
      return next;
    });

    const releaseInflightLayer = () => {
      kernelHandle.setInflightLayerIds((previous) => {
        if (!previous.has(layerId)) return previous;
        const next = new Set(previous);
        next.delete(layerId);
        return next;
      });
    };

    void (async () => {
      try {
        if (isSvg) {
          kernelHandle.setLayer(layerId, (targetLayer) => ({
            ...targetLayer,
            imageData: null,
            mustRender: false,
            loadError: false,
          }));
          kernelHandle.reportAssetLoaded(source);
          return;
        }

        let hydratedVideoBlob: Blob | null = null;
        let imageData: ImageData;
        if (isVideo) {
          // Download the video bytes once; the retained blob becomes the layer's buffer.
          // Dimensions are best-effort — if metadata couldn't be read, fall back to 16:9 so the
          // placeholder is sane; the real <video> reports true dimensions once it plays.
          const { blob, width, height } = await loadVideoBlobAndMetadata(source);
          hydratedVideoBlob = blob;
          const videoWidth = Number.isFinite(width) && width > 0 ? width : 1920;
          const videoHeight = Number.isFinite(height) && height > 0 ? height : 1080;
          imageData = new ImageData(Math.max(1, Math.round(videoWidth)), Math.max(1, Math.round(videoHeight)));
        } else {
          imageData = await loadImageDataFromUrl(source);
        }

        kernelHandle.setLayer(layerId, (targetLayer) => {
          const currentSource = isVideo ? targetLayer.videoSrc : targetLayer.imageSrc;
          if (currentSource !== source) {
            return targetLayer;
          }

          const resolvedScale = resolveRasterLayerScaleForHydration(
            targetLayer,
            imageData.width,
            imageData.height
          );
          const mustRepairRectFromIntrinsic = isLegacyInvalidRasterRect(targetLayer.rect);
          const repairedRect = mustRepairRectFromIntrinsic
            ? {
                ...targetLayer.rect,
                w: imageData.width * resolvedScale,
                h: imageData.height * resolvedScale,
              }
            : targetLayer.rect;

          return syncRasterLayerRectSize({
            ...targetLayer,
            imageData,
            ...(isVideo ? { videoBlob: hydratedVideoBlob } : {}),
            rect: repairedRect,
            scale: resolvedScale,
            mustRender: false,
            loadError: false,
          });
        });

        kernelHandle.reportAssetLoaded(source);
      } catch (error) {
        console.error(`[Layers] Failed to force-reload layer ${layerToReload?.type ?? 'image'} ${layerId} from ${source}`, error);
        kernelHandle.setLayer(layerId, (existing) => ({ ...existing, loadError: true, mustRender: false }));
        kernelHandle.reportAssetLoaded(source);
      } finally {
        releaseInflightLayer();
      }
    })();

    if (projectId) {
      kernelHandle.hydrateVisibleLayers(projectId);
    }
  }, [kernel, getLayerById]);

  const hydrateProjectLayerPreviews = useCallback((
    projectIdInput: string,
    layerIds?: string[],
    options?: { retry?: boolean },
  ): void => {
    if (typeof window === 'undefined' || !kernel.current) return;
    const projectId = projectIdInput.trim();
    if (!projectId) return;
    void runProjectLayerPreviewHydration({
      projectId,
      layers: Object.values(kernel.current.getLayerRegistry()),
      requestedLayerIds: layerIds ? new Set(layerIds.map((layerId) => layerId.trim()).filter(Boolean)) : null,
      retry: options?.retry,
      inflightLayerIds: projectPreviewInflightLayerIds.current,
      cacheEntries: projectPreviewCacheEntries.current,
      deferredLayerSources: projectPreviewDeferredLayerSources.current,
      loadPixels: loadImageDataFromUrl,
      setLayer: (layerId, updater) => kernel.current?.setLayer(layerId, updater),
      reportAssetLoaded: (source) => kernel.current?.reportAssetLoaded(source),
    });
  }, [kernel]);

  const hydrateVisibleLayers = useCallback((projectIdInput: string) => {
    if (typeof window === 'undefined' || !kernel.current) {
      return;
    }
    const projectId = projectIdInput.trim();
    if (!projectId) return;

    const kernelHandle = kernel.current;
    // The marketing view renders Project Layer output inventory from asset URLs. Hydrating every
    // output into an ImageData buffer wastes hundreds of MB and can crash the tab. The projectType
    // fallback keeps legacy callers safe before the persistent view runtime is hydrated.
    if (getProjectStudioViewMode(kernelHandle, projectId) === 'marketing') return;
    const layers = listLayersByProjectId(projectId);
    const { inflightLayerIds } = kernelHandle.getState();
    
    const inflightSnapshot = new Set(inflightLayerIds);
    const candidates: Layer[] = [];

    for (const layer of layers) {
      if (!layer.visible) continue;

      if (layer.type === 'svg') {
        const source = typeof layer.imageSrc === 'string' ? layer.imageSrc.trim() : '';
        if (source) {
          kernelHandle.reportAssetLoaded(source);
        }
        continue;
      }
      
      // Determine source based on type
      const source = layer.type === 'video' ? layer.videoSrc : layer.imageSrc;
      
      // Gatekeeping. For video the real payload is the downloaded blob (videoBlob), NOT the
      // imageData — which is only a dimension placeholder. Gate video on videoBlob so a layer
      // carrying a stale placeholder from an earlier metadata-only pass still hydrates its bytes.
      if (!source) continue;
      const alreadyHydrated = layer.type === 'video' ? Boolean(layer.videoBlob) : Boolean(layer.imageData);
      if (alreadyHydrated || layer.loadError) continue;
      // A layer with no workspace scope can't be written back through setLayer's guard, so hydrating
      // it would only throw. Skip it instead of re-fetching + re-attempting it on every pass.
      if (typeof layer.layerWorkspaceId !== 'string' || layer.layerWorkspaceId.trim().length === 0) continue;
      if (inflightSnapshot.has(layer.id)) continue;
      
      inflightSnapshot.add(layer.id);
      candidates.push(layer);
    }

    if (candidates.length === 0) {
      return;
    }

    kernelHandle.setInflightLayerIds(inflightSnapshot);

    const releaseInflightLayer = (layerId: string) => {
      kernelHandle.setInflightLayerIds((prev) => {
        const next = new Set(prev);
        next.delete(layerId);
        return next;
      });
    };

    const hydrateSingleLayer = async (layer: Layer): Promise<void> => {
      const isVideo = layer.type === 'video';
      const source = isVideo ? layer.videoSrc : layer.imageSrc;

      if (!source) {
        releaseInflightLayer(layer.id);
        return;
      }

      // Keep the 60s essential-assets failsafe aligned with actual queue progress:
      // every time a new tracked download starts, restart the countdown.
      const pendingAssetUrls = kernelHandle.getPendingAssetUrls();
      if (pendingAssetUrls.size > 0 && pendingAssetUrls.has(source)) {
        kernelHandle.startAssetLoadingFailsafe();
      }

      try {
        let hydratedVideoBlob: Blob | null = null;
        let imageData: ImageData;
        if (isVideo) {
          // Download the video bytes once; the retained blob becomes the layer's buffer.
          // Dimensions are best-effort — if metadata couldn't be read, fall back to 16:9 so the
          // placeholder is sane; the real <video> reports true dimensions once it plays.
          const { blob, width, height } = await loadVideoBlobAndMetadata(source);
          hydratedVideoBlob = blob;
          const videoWidth = Number.isFinite(width) && width > 0 ? width : 1920;
          const videoHeight = Number.isFinite(height) && height > 0 ? height : 1080;
          imageData = new ImageData(Math.max(1, Math.round(videoWidth)), Math.max(1, Math.round(videoHeight)));
        } else {
          imageData = await loadImageDataFromUrl(source);
        }

        kernelHandle.setLayer(layer.id, (targetLayer) => {
          const currentSource = isVideo ? targetLayer.videoSrc : targetLayer.imageSrc;
          if (currentSource !== source) {
            return targetLayer;
          }

          const resolvedScale = resolveRasterLayerScaleForHydration(
            targetLayer,
            imageData.width,
            imageData.height
          );
          const mustRepairRectFromIntrinsic = isLegacyInvalidRasterRect(targetLayer.rect);
          const repairedRect = mustRepairRectFromIntrinsic
            ? {
                ...targetLayer.rect,
                w: imageData.width * resolvedScale,
                h: imageData.height * resolvedScale,
              }
            : targetLayer.rect;

          return syncRasterLayerRectSize({
            ...targetLayer,
            imageData,
            ...(isVideo ? { videoBlob: hydratedVideoBlob } : {}),
            rect: repairedRect,
            scale: resolvedScale,
            mustRender: false,
            loadError: false,
          });
        });

        kernelHandle.reportAssetLoaded(source);
      } catch (error) {
        console.error(`[Layers] Failed to hydrate layer ${layer.type} ${layer.id} from ${source}`, error);

        // Flagging loadError goes through setLayer's workspace-scope guard, which throws for a layer
        // with an empty layerWorkspaceId. Never let that re-throw escape: it would reject this whole
        // hydration worker and leave sibling candidates (e.g. a fresh generation result) unhydrated.
        try {
          kernelHandle.setLayer(layer.id, (existing) => ({ ...existing, loadError: true, mustRender: false }));
        } catch (flagError) {
          console.error(`[Layers] Could not flag loadError for ${layer.id} (likely missing workspace scope); skipping to keep hydration alive`, flagError);
        }
        kernelHandle.reportAssetLoaded(source);
      } finally {
        releaseInflightLayer(layer.id);
      }
    };

    const workerCount = Math.min(MAX_PARALLEL_LAYER_HYDRATION, candidates.length);
    let nextCandidateIndex = 0;

    const workers = Array.from({ length: workerCount }, async () => {
      while (true) {
        const index = nextCandidateIndex++;
        const layer = candidates[index];
        if (!layer) {
          break;
        }
        await hydrateSingleLayer(layer);
      }
    });

    void Promise.all(workers);
  }, [kernel, listLayersByProjectId]);


  const updateLayerName = useCallback((layerId: string, newName: string) => {
    if (!kernel.current) return;
    kernel.current.setLayer(layerId, (layer) => {
      kernel.current?.logUserAction('Layer Name Changed', { layer_id: layerId, old_name: layer.name, new_name: newName });
      return { ...layer, name: newName };
    });
  }, [kernel]);

  const selectLayer = useCallback((projectIdInput: string, layerId: string, isShiftClick: boolean) => {
    if (!kernel.current) return;
    const projectId = projectIdInput.trim();
    if (!projectId) return;
    const targetLayer = getLayerById(layerId);
    if (!targetLayer || !targetLayer.layerWorkspaceId) return;

    const targetWorkspaceId = targetLayer.layerWorkspaceId;

    // Rule #2 & #3: If the layer is in a different workspace, always switch to it.
    if (targetWorkspaceId !== kernel.current.getProjectUIWorkspaceId(projectId)) {
      kernel.current?.logUserAction('Workspace Switched by Layer Selection', { 
        target_workspace_id: targetWorkspaceId, 
        source_layer_id: layerId 
      });
      kernel.current?.setProjectUIWorkspaceId(projectId, targetWorkspaceId);
      // After switching, this layer becomes the only selected one.
      setProjectSelectedLayersId(kernel.current, projectId, [layerId]);
      bumpLayerPulseToken(layerId);
      kernel.current?.logUserAction('Layer Selected', { layer_id: layerId, layer_name: targetLayer.name, selection_mode: 'single_in_new_workspace' });
      return;
    }

    // If we are in the correct workspace, proceed with normal selection logic.
    const currentSelection = getProjectSelectedLayersId(kernel.current, projectId);
    let newSelection: string[];
    if (isShiftClick) {
      // Shift-click logic (already in the correct workspace)
      if (currentSelection.includes(layerId)) {
        newSelection = currentSelection.filter(id => id !== layerId); // Deselect
      } else {
        newSelection = [...currentSelection, layerId]; // Add to selection
      }
    } else {
      // Normal click: select only this layer
      newSelection = [layerId];
    }

    setProjectSelectedLayersId(kernel.current, projectId, newSelection);
    kernel.current?.logUserAction('Layer Selected', {
      layer_id: layerId,
      layer_name: targetLayer.name,
      is_shift_click: isShiftClick,
      new_selection_count: newSelection.length,
      selection_mode: isShiftClick ? 'multi' : 'single'
    });
    if (newSelection.includes(layerId)) {
      bumpLayerPulseToken(layerId);
    }
  }, [kernel, getLayerById, bumpLayerPulseToken]);

  const reorderLayer = useCallback((projectIdInput: string, draggedId: string, targetId: string, dropOnUpperHalf: boolean) => {
    if (!kernel.current) return;
    const projectId = projectIdInput.trim();
    if (!projectId) return;

    const layersBefore = listLayersByProjectId(projectId);

    const draggedLayer = layersBefore.find(l => l.id === draggedId);
    const targetLayer = layersBefore.find(l => l.id === targetId);

    if (!draggedLayer || !targetLayer || !draggedLayer.layerWorkspaceId || !targetLayer.layerWorkspaceId) return;

    const sourceWorkspaceId = draggedLayer.layerWorkspaceId;
    const targetWorkspaceId = targetLayer.layerWorkspaceId;

    let layersAfter: Layer[];
    let actionDescription = "Reorder Layer";

    if (sourceWorkspaceId !== targetWorkspaceId) {
      actionDescription = "Move and Reorder Layer";
      const draggedIndex = layersBefore.findIndex(l => l.id === draggedId);
      if (draggedIndex < 0) return;

      const updatedLayer = {
        ...draggedLayer,
        layerWorkspaceId: targetWorkspaceId,
        mustRender: true,
      };

      const tempLayers = [...layersBefore];
      tempLayers.splice(draggedIndex, 1);

      const newTargetIndex = tempLayers.findIndex(l => l.id === targetId);
      const insertionIndex = dropOnUpperHalf ? newTargetIndex + 1 : newTargetIndex;
      tempLayers.splice(insertionIndex, 0, updatedLayer);
      layersAfter = tempLayers;
    } else {
      // Same-Workspace Drop Logic
      const tempLayers = [...layersBefore];
      const draggedIndex = tempLayers.findIndex(layer => layer.id === draggedId);
      if (draggedIndex < 0) return;

      const [removedLayer] = tempLayers.splice(draggedIndex, 1);
      const newTargetIndex = tempLayers.findIndex(l => l.id === targetId);
      
      const insertionIndex = dropOnUpperHalf ? newTargetIndex + 1 : newTargetIndex;
      if (removedLayer) tempLayers.splice(insertionIndex, 0, removedLayer);
      
      layersAfter = tempLayers;
    }

    const workspaceIdsToResequence =
      sourceWorkspaceId === targetWorkspaceId
        ? [sourceWorkspaceId]
        : [sourceWorkspaceId, targetWorkspaceId];
    const orderedLayersAfter = resequenceRenderOrderByArray(layersAfter, workspaceIdsToResequence);
    const deltaRedo = computeLayerDelta(layersBefore, orderedLayersAfter);
    const deltaUndo = computeLayerDelta(orderedLayersAfter, layersBefore);

    kernel.current.executeAction({
      description: actionDescription,
      undo: () => {
        if (!kernel.current) return;
        applyLayerDelta(deltaUndo);
        syncWorkspaceLayerMembership([sourceWorkspaceId, targetWorkspaceId]);
        kernel.current.invalidateWorkspaceVersionCache(sourceWorkspaceId);
        if (sourceWorkspaceId !== targetWorkspaceId) { // If it was a cross-workspace move, switch UI back on undo
          kernel.current.invalidateWorkspaceVersionCache(targetWorkspaceId);
          kernel.current.setProjectUIWorkspaceId(projectId, sourceWorkspaceId);
        }
      },
      redo: () => {
        if (!kernel.current) return;
        kernel.current.logUserAction('Layer Reordered', {
          dragged_layer_id: draggedId,
          target_layer_id: targetId,
          source_workspace_id: sourceWorkspaceId,
          target_workspace_id: targetWorkspaceId,
        });
        applyLayerDelta(deltaRedo);
        syncWorkspaceLayerMembership([sourceWorkspaceId, targetWorkspaceId]);
        kernel.current.invalidateWorkspaceVersionCache(sourceWorkspaceId);
        if (sourceWorkspaceId !== targetWorkspaceId) { // If it was a cross-workspace move, switch UI on redo
          kernel.current.invalidateWorkspaceVersionCache(targetWorkspaceId);
          kernel.current.setProjectUIWorkspaceId(projectId, targetWorkspaceId);
        }
      },
    });
  }, [kernel, applyLayerDelta, computeLayerDelta, listLayersByProjectId, syncWorkspaceLayerMembership]);

  const copySelectedLayersToClipboard = useCallback((projectId: string) => {
    const selectedLayerIds =
      kernel.current && projectId
        ? getProjectSelectedLayersId(kernel.current, projectId)
        : [];
    if (selectedLayerIds.length === 0) {
      kernel.current?.logUserAction('Copy Layers to Clipboard Attempted', { count: 0, reason: 'No layers selected' });
      return;
    }

    const layersToCopy = selectedLayerIds
      .map((layerId) => kernel.current?.getLayerRegistry()[layerId])
      .filter((layer): layer is Layer => Boolean(layer))
      .map((layer) => {
        const layerData: Partial<Layer> & { isFromClipboard?: boolean } = {
          imageSrc: layer.imageSrc,
          rect: { ...layer.rect },
          scale: layer.scale,
          visible: layer.visible,
          name: layer.name,
          locked: layer.locked,
          opacity: layer.opacity,
          type: layer.type,
          params: layer.params,
          source: layer.source,
          isFromClipboard: true,
        };

        if (!layerData.imageSrc && layer.imageData) {
          layerData.imageData = layer.imageData;
        }
        return layerData;
      });

    if (layersToCopy.length === 0) return;

    void copyTextToClipboard(JSON.stringify(layersToCopy, null, 2))
      .then(() => {
        kernel.current?.logUserAction('Layers Copied to Clipboard', { count: layersToCopy.length });
      })
      .catch((err: unknown) => {
        kernel.current?.handleError('Failed to copy layers to clipboard!');
        kernel.current?.logUserAction('Copy Layers to Clipboard Failed', { count: layersToCopy.length, error: err instanceof Error ? err.message : 'Unknown' });
      });
  }, [kernel]);

  const restoreLayerConfig = useCallback((layerId: string) => {
    if (!kernel.current) return;

    const layer = getLayerById(layerId);
    if (!layer || !layer.params || !layer.source) {
      kernel.current.logUserAction('Layer Config Restore Failed', { layer_id: layerId, reason: 'Layer, params, or source not found' });
      return;
    }

    const modelSource = layer.source;

    kernel.current.setModelParams(prevModelParams => {
      const defaultGetter = getDefaultParamsForModel(modelSource);
      if (!defaultGetter) {
        console.error(`Unsupported model for config restore: ${modelSource}`);
        return prevModelParams;
      }
      const defaultParams = defaultGetter();

      const updatedParams = {
        ...defaultParams,
        ...layer.params,
      } as DiffusionParams;

      return { ...prevModelParams, [modelSource]: updatedParams };
    });

    kernel.current.setDiffusionModel(modelSource);
    kernel.current.logUserAction('Layer Config Restored', {
      layer_id: layer.id,
      layer_name: layer.name,
      restored_model_id: modelSource,
      restored_model_name: modelMappings[modelSource]?.name
    });
  }, [kernel, getLayerById]);

  const downloadLayer = useCallback((layerId: string) => {
    const layerToDownload = getLayerById(layerId);
    if (!layerToDownload) return;

    const fileNameBase = (layerToDownload.name || `layer-${layerToDownload.id}`).replace(/[\\/*?:"<>|]/g, '_');

    if (layerToDownload.type === 'svg' || getDrawingLayerPayload(layerToDownload.params)) {
        const source = getDrawingAwareSvgLayerSource(layerToDownload);
        if (!source) {
            console.error("No SVG content available to download for this layer.");
            return;
        }
        const downloadLink = document.createElement('a');
        downloadLink.href = source;
        downloadLink.download = `${fileNameBase}.svg`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
    } else if ((layerToDownload.type === 'video' || layerToDownload.type === 'audio') && layerToDownload.videoSrc) {
        // Handle video/audio source download
        try {
            const downloadLink = document.createElement('a');
            downloadLink.href = layerToDownload.videoSrc;
            downloadLink.download = `${fileNameBase}.mp4`;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
        } catch (error) {
            console.error("Error triggering media download:", error);
        }
    } else if (layerToDownload.imageData) {
        // Handle image download
        const canvas = document.createElement('canvas');
        canvas.width = layerToDownload.imageData.width;
        canvas.height = layerToDownload.imageData.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.putImageData(layerToDownload.imageData, 0, 0);

        canvas.toBlob((blob) => {
            if (!blob) {
                kernel.current?.handleError("Failed to download layer image.");
                return;
            }
            void signImageExport(blob).then((signed) => {
                const objectUrl = URL.createObjectURL(signed);
                const downloadLink = document.createElement('a');
                downloadLink.href = objectUrl;
                downloadLink.download = `${fileNameBase}.webp`;
                document.body.appendChild(downloadLink);
                downloadLink.click();
                document.body.removeChild(downloadLink);
                setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
            });
        }, 'image/webp');
    } else {
        console.error("No content available to download for this layer.");
    }
  }, [kernel, getLayerById]);

  const duplicateLayer = useCallback((layerId: string) => {
    if (!kernel.current) return;
    const originalLayer = getLayerById(layerId);
    if (!originalLayer || !originalLayer.layerWorkspaceId) return;
    const workspaceId = originalLayer.layerWorkspaceId;
    const projectId = requireWorkspaceProjectIdFromKernel(kernel, workspaceId, 'useLayers');
    const currentLayers = projectId
      ? listLayersByProjectId(projectId)
      : getLayersInWorkspaceContext(workspaceId);

    let newLayerName = '';
    const originalName = originalLayer.name;
    const numberSuffixRegex = /(?: )(\d+)$/;
    const numberMatch = originalName.match(numberSuffixRegex);

    if (numberMatch) {
      const baseName = originalName.substring(0, numberMatch.index);
      const number = parseInt(numberMatch[1] || '', 10);
      newLayerName = `${baseName} ${number + 1}`;
    } else {
      const letterSuffixRegex = /[a-zA-Z]$/;
      if (letterSuffixRegex.test(originalName) || !originalName.match(/\d$/)) {
        newLayerName = `${originalName} 2`;
      } else {
        newLayerName = `${originalName} 2`;
      }
    }

    const duplicatedLayer: Layer = syncRasterLayerRectSize({
      ...originalLayer,
      id: createLayerId(),
      timestamp: Date.now(),
      name: newLayerName,
      contentVersion: Date.now(),
      layerWorkspaceId: originalLayer.layerWorkspaceId,
      renderOrder: originalLayer.renderOrder,
    });

    const originalLayerIndex = currentLayers.findIndex(layer => layer.id === layerId);
    const updatedLayers = [...currentLayers];
    updatedLayers.splice(originalLayerIndex + 1, 0, duplicatedLayer);
    const resequencedLayers = resequenceRenderOrderByArray(updatedLayers, [originalLayer.layerWorkspaceId]);
    const delta = computeLayerDelta(currentLayers, resequencedLayers);
    applyLayerDelta(delta);
    const workspaceLayerIdsAfter = resequencedLayers
      .filter((layer) => layer.layerWorkspaceId === originalLayer.layerWorkspaceId)
      .map((layer) => layer.id);
    const workspaceProjectId = requireWorkspaceProjectIdFromKernel(kernel, originalLayer.layerWorkspaceId, 'useLayers');
    kernel.current.setWorkspace(originalLayer.layerWorkspaceId, (workspace) => ({
      ...workspace,
      layerIds: [...workspaceLayerIdsAfter],
    }), workspaceProjectId);

    kernel.current?.logUserAction('Layer Duplicated', {
      original_layer_id: layerId,
      original_layer_name: originalLayer.name,
      duplicated_layer_id: duplicatedLayer.id,
      duplicated_layer_name: duplicatedLayer.name,
      workspace_id: duplicatedLayer.layerWorkspaceId
    });
    kernel.current?.invalidateWorkspaceVersionCache(originalLayer.layerWorkspaceId);
    persistLayerCreate(duplicatedLayer, 'useLayers.duplicateLayer');
  }, [kernel, applyLayerDelta, computeLayerDelta, getLayerById, listLayersByProjectId, getLayersInWorkspaceContext, persistLayerCreate]);

  const mergeLayersCanvas = useCallback(async (layerA: Layer, layerB: Layer): Promise<Layer> => {
    if (!kernel.current) throw new Error("Editor context not available.");

    const layerAWorkspaceId = getLayerWorkspaceId(layerA);
    const layerBWorkspaceId = getLayerWorkspaceId(layerB);
    if (!layerAWorkspaceId || !layerBWorkspaceId || layerAWorkspaceId !== layerBWorkspaceId) {
      throw new Error('Cannot merge layers from different workspace scopes.');
    }
    const workspace = kernel.current.getWorkspace(layerAWorkspaceId);
    if (!workspace) throw new Error(`Workspace ${layerAWorkspaceId} not found for merging layers.`);
    const layerAProjectId = layerA.projectId?.trim() || workspace.projectId.trim();
    const layerBProjectId = layerB.projectId?.trim() || workspace.projectId.trim();
    if (!layerAProjectId || layerAProjectId !== layerBProjectId || layerAProjectId !== workspace.projectId.trim()) {
      throw new Error('Cannot merge layers from different project scopes.');
    }
    const frameRect = workspace.frameRect;

    const getLayerRenderData = (layer: Layer): {
      imageData: ImageData;
      worldRect: Rect;
      drawRect: Rect;
      transform: ReturnType<typeof resolveStudioLayerTransform>;
    } | null => {
      if (!layer.imageData) return null;
      const scale = layer.scale ?? 1;
      const drawRect = {
        x: frameRect.x + layer.rect.x,
        y: frameRect.y + layer.rect.y,
        w: layer.imageData.width * scale,
        h: layer.imageData.height * scale,
      };
      return {
        imageData: layer.imageData,
        worldRect: getTransformedRectBounds(drawRect, resolveStudioLayerTransform(layer.params)),
        drawRect,
        transform: resolveStudioLayerTransform(layer.params),
      };
    };

    const dataA = getLayerRenderData(layerA);
    const dataB = getLayerRenderData(layerB);

    if (!dataA && dataB) return { ...layerB, id: createLayerId(), timestamp: Date.now() };
    if (dataA && !dataB) return { ...layerA, id: createLayerId(), timestamp: Date.now() };
    if (!dataA || !dataB) return layerA;

    const { imageData: imageDataA, worldRect: worldRectA } = dataA;
    const { imageData: imageDataB, worldRect: worldRectB } = dataB;

    const scaleA = dataA.drawRect.w > 0 ? dataA.drawRect.w / imageDataA.width : 1;
    const scaleB = dataB.drawRect.w > 0 ? dataB.drawRect.w / imageDataB.width : 1;

    let upscaleFactor = 1;
    const minScale = Math.min(scaleA, scaleB);
    if (minScale < 1 && minScale > 0) {
      upscaleFactor = 1 / minScale;
    }

    const upscaledRectA = {
      x: dataA.drawRect.x * upscaleFactor,
      y: dataA.drawRect.y * upscaleFactor,
      width: dataA.drawRect.w * upscaleFactor,
      height: dataA.drawRect.h * upscaleFactor,
    };
    const upscaledRectB = {
      x: dataB.drawRect.x * upscaleFactor,
      y: dataB.drawRect.y * upscaleFactor,
      width: dataB.drawRect.w * upscaleFactor,
      height: dataB.drawRect.h * upscaleFactor,
    };
    const upscaledBoundsA = {
      x: worldRectA.x * upscaleFactor,
      y: worldRectA.y * upscaleFactor,
      width: worldRectA.w * upscaleFactor,
      height: worldRectA.h * upscaleFactor,
    };
    const upscaledBoundsB = {
      x: worldRectB.x * upscaleFactor,
      y: worldRectB.y * upscaleFactor,
      width: worldRectB.w * upscaleFactor,
      height: worldRectB.h * upscaleFactor,
    };

    const unionMinX_upscaled = Math.min(upscaledBoundsA.x, upscaledBoundsB.x);
    const unionMaxX_upscaled = Math.max(upscaledBoundsA.x + upscaledBoundsA.width, upscaledBoundsB.x + upscaledBoundsB.width);
    const unionMinY_upscaled = Math.min(upscaledBoundsA.y, upscaledBoundsB.y);
    const unionMaxY_upscaled = Math.max(upscaledBoundsA.y + upscaledBoundsA.height, upscaledBoundsB.y + upscaledBoundsB.height);

    const unionWorldWidth = unionMaxX_upscaled - unionMinX_upscaled;
    const unionWorldHeight = unionMaxY_upscaled - unionMinY_upscaled;

    let mergedImagePixelWidth = Math.round(unionWorldWidth);
    let mergedImagePixelHeight = Math.round(unionWorldHeight);

    let downscaleFactor = 1;
    if (mergedImagePixelWidth > STUDIO_LAYER_MAX_SIDE_PIXELS || mergedImagePixelHeight > STUDIO_LAYER_MAX_SIDE_PIXELS) {
      downscaleFactor = Math.max(mergedImagePixelWidth / STUDIO_LAYER_MAX_SIDE_PIXELS, mergedImagePixelHeight / STUDIO_LAYER_MAX_SIDE_PIXELS);
      mergedImagePixelWidth = Math.floor(mergedImagePixelWidth / downscaleFactor);
      mergedImagePixelHeight = Math.floor(mergedImagePixelHeight / downscaleFactor);
    }

    const canvas = new OffscreenCanvas(mergedImagePixelWidth, mergedImagePixelHeight);
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error("Canvas context not supported");
    }

    const drawLayer = (
      imageData: ImageData,
      upscaledRect: { x: number; y: number; width: number; height: number },
      transform: ReturnType<typeof resolveStudioLayerTransform>,
    ) => {
      const tempCanvas = new OffscreenCanvas(imageData.width, imageData.height);
      const tempCtx = tempCanvas.getContext('2d');
      if (!tempCtx) return;
      tempCtx.putImageData(imageData, 0, 0);
      const drawX = (upscaledRect.x - unionMinX_upscaled) / downscaleFactor;
      const drawY = (upscaledRect.y - unionMinY_upscaled) / downscaleFactor;
      const drawW = upscaledRect.width / downscaleFactor;
      const drawH = upscaledRect.height / downscaleFactor;
      drawImageWithStudioLayerTransform(ctx, tempCanvas, drawX, drawY, drawW, drawH, transform);
    };

    drawLayer(imageDataB, upscaledRectB, dataB.transform);
    drawLayer(imageDataA, upscaledRectA, dataA.transform);
    
    const mergedImageData = ctx.getImageData(0, 0, mergedImagePixelWidth, mergedImagePixelHeight);

    const originalUnionMinX = unionMinX_upscaled / upscaleFactor;
    const originalUnionMinY = unionMinY_upscaled / upscaleFactor;

    const mergedLayer: Layer = {
      id: createLayerId(),
      projectId: layerAProjectId,
      timestamp: Date.now(),
      imageData: mergedImageData,
      imageSrc: undefined,
      rect: {
        x: originalUnionMinX - frameRect.x,
        y: originalUnionMinY - frameRect.y,
        w: mergedImageData.width * (1 / upscaleFactor * downscaleFactor),
        h: mergedImageData.height * (1 / upscaleFactor * downscaleFactor),
      },
      scale: 1 / upscaleFactor * downscaleFactor,
      visible: true,
      name: layerB.name ? layerB.name : layerA.name,
      locked: true,
      opacity: 1,
      layerWorkspaceId: layerAWorkspaceId,
      source: 'merge',
      contentVersion: Date.now(),
      renderOrder: layerB.renderOrder,
    };

    return mergedLayer;
  }, [kernel]);

  const mergeGeneratedLayerIntoAnchorLayer = useCallback(async (
    generatedLayer: Layer,
    anchorLayerId: string,
    options?: {
      description?: string;
      isUndoable?: boolean;
      params?: Layer['params'];
    }
  ): Promise<Layer | null> => {
    if (!kernel.current) return null;
    const normalizedAnchorLayerId =
      typeof anchorLayerId === 'string' ? anchorLayerId.trim() : '';
    if (!normalizedAnchorLayerId) return null;

    const anchorLayer = getLayerById(normalizedAnchorLayerId);
    if (!anchorLayer) {
      console.warn(
        `[mergeGeneratedLayerIntoAnchorLayer] Anchor layer "${normalizedAnchorLayerId}" not found.`
      );
      return null;
    }

    const mergedLayerBase = await mergeLayersCanvas(generatedLayer, anchorLayer);
    const timestampIso = new Date().toISOString();
    const mergedAnchorLayer: Layer = {
      ...anchorLayer,
      ...mergedLayerBase,
      id: anchorLayer.id,
      projectId: anchorLayer.projectId?.trim() || mergedLayerBase.projectId,
      name: anchorLayer.name,
      layerWorkspaceId: anchorLayer.layerWorkspaceId,
      renderOrder: anchorLayer.renderOrder,
      source: anchorLayer.source,
      type: anchorLayer.type,
      visible: anchorLayer.visible,
      locked: anchorLayer.locked,
      opacity: anchorLayer.opacity,
      params: mergeStudioLayerTransformParams(options?.params ?? anchorLayer.params, {
        rotationDeg: 0,
        flipX: false,
        flipY: false,
      }),
      videoSrc: anchorLayer.videoSrc,
      videoFramesUrls: anchorLayer.videoFramesUrls,
      imageSrc: undefined,
      timestamp: Date.now(),
      contentVersion: Date.now(),
      modificationLog: mergeModificationLogs(anchorLayer.modificationLog, undefined, [{
        timestamp: timestampIso,
        type: 'CREATED_FROM_MERGE',
        description: `Merged generated tile into '${anchorLayer.name || anchorLayer.id}'`,
      }]),
    };

    const applyLayer = (layer: Layer) => {
      kernel.current?.setLayer(normalizedAnchorLayerId, layer);
      kernel.current?.invalidateWorkspaceVersionCache(anchorLayer.layerWorkspaceId);
    };

    if (options?.isUndoable === false) {
      applyLayer(mergedAnchorLayer);
      return mergedAnchorLayer;
    }

    const description =
      typeof options?.description === 'string' && options.description.trim().length > 0
        ? options.description
        : `Merge tile into ${anchorLayer.name || anchorLayer.id}`;

    kernel.current.executeAction({
      description,
      redo: () => {
        applyLayer(mergedAnchorLayer);
      },
      undo: () => {
        applyLayer(anchorLayer);
      },
    });

    return mergedAnchorLayer;
  }, [kernel, getLayerById, mergeLayersCanvas]);

  const paintLayerIntoGridSection = useCallback<KernelHandle['paintLayerIntoGridSection']>(async (tileLayer, target) => {
    const k = kernel.current;
    if (!k) return null;
    const workspace = k.getWorkspace(target.workspaceId);
    if (!workspace) throw new Error(`Workspace ${target.workspaceId} not found for grid section.`);
    if (!tileLayer.imageData) throw new Error('Grid tile has no pixels to paint.');
    const existing = target.layerId ? getLayerById(target.layerId) : null;
    // Painting over an unloaded anchor would replace everything it holds with this tile alone.
    if (target.layerId && !existing?.imageData) throw new Error('Grid section pixels are not loaded yet.');

    const { section } = target;
    const density = section.pixelWidth / section.rect.w;
    const canvas = new OffscreenCanvas(section.pixelWidth, section.pixelHeight);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas context not supported');
    if (existing?.imageData) ctx.putImageData(existing.imageData, 0, 0);

    const tileCanvas = new OffscreenCanvas(tileLayer.imageData.width, tileLayer.imageData.height);
    tileCanvas.getContext('2d')?.putImageData(tileLayer.imageData, 0, 0);
    const tileScale = tileLayer.scale ?? 1;
    drawImageWithStudioLayerTransform(
      ctx,
      tileCanvas,
      (workspace.frameRect.x + tileLayer.rect.x - section.rect.x) * density,
      (workspace.frameRect.y + tileLayer.rect.y - section.rect.y) * density,
      tileLayer.imageData.width * tileScale * density,
      tileLayer.imageData.height * tileScale * density,
      resolveStudioLayerTransform(tileLayer.params),
    );
    const imageData = ctx.getImageData(0, 0, section.pixelWidth, section.pixelHeight);

    if (!existing) {
      return addNewLayer(target.workspaceId, imageData, {
        name: target.name,
        source: 'merge',
        skipRender: true,
        params: target.params,
        scale: 1 / density,
        rect: {
          x: section.rect.x - workspace.frameRect.x,
          y: section.rect.y - workspace.frameRect.y,
          w: section.rect.w,
          h: section.rect.h,
        },
      });
    }

    const painted: Layer = {
      ...existing,
      imageData,
      imageSrc: undefined,
      params: target.params,
      timestamp: Date.now(),
      contentVersion: Date.now(),
    };
    k.setLayer(existing.id, painted);
    k.invalidateWorkspaceVersionCache(target.workspaceId);
    return painted;
  }, [kernel, getLayerById, addNewLayer]);

  const mergeLayerDown = useCallback(async (layerId: string) => {
    if (!kernel.current) return;

    const topLayerRef = getLayerById(layerId);
    if (!topLayerRef || !topLayerRef.layerWorkspaceId) {
      console.warn(`[mergeLayerDown] Top layer with ID ${layerId} not found in a workspace.`);
      return;
    }
    const workspaceId = topLayerRef.layerWorkspaceId;
    const projectId = requireWorkspaceProjectIdFromKernel(kernel, workspaceId, 'useLayers');
    const listScopedLayers = () =>
      projectId
        ? listLayersByProjectId(projectId)
        : getLayersInWorkspaceContext(workspaceId);

    // --- CAPTURE STATE BEFORE MODIFICATION ---
    const allLayers = listScopedLayers();
    const topLayerIndex = allLayers.findIndex(l => l.id === layerId);
    if (topLayerIndex === -1) {
      console.warn(`[mergeLayerDown] Top layer with ID ${layerId} not found.`);
      return;
    }

    const topLayer = allLayers[topLayerIndex];
    if (!topLayer) return;

    const workspaceLayers = kernel.current.getLayersInWorkspaceContext(topLayer.layerWorkspaceId ?? '');
    const workspaceLayerIndex = workspaceLayers.findIndex(l => l.id === layerId);

    if (workspaceLayerIndex <= 0) {
      // It's the bottom-most layer in its workspace, nothing to merge down to.
      return;
    }

    const layerBelow = workspaceLayers[workspaceLayerIndex - 1];
    if (!layerBelow) {
      console.warn(`[mergeLayerDown] Layer below not found for layer ID ${layerId}.`);
      return;
    }

    // This variable will be captured in the closure for both redo and undo.
    let mergedLayerBase: Layer | null = null;
    const originalTopLayerLog = cloneModificationLog(topLayer.modificationLog);
    const originalBelowLayerLog = cloneModificationLog(layerBelow.modificationLog);

    let hasEmittedMergeTimelineEvent = false;
    kernel.current.executeAction({
      description: `Merge Layer Down: ${topLayer.name || `Layer ${topLayer.id}`}`,
      redo: async () => {
        if (!kernel.current) return;
        if (!mergedLayerBase) {
          mergedLayerBase = await mergeLayersCanvas(topLayer, layerBelow);
        }
        const timestamp = new Date().toISOString();
        const createdFromEntry: LayerModificationLogEntry = {
          timestamp,
          type: 'CREATED_FROM_MERGE',
          description: `Created from merge with layer '${topLayer.name}'`,
        };
        const mergedLayerWithLog: Layer = {
          ...mergedLayerBase,
          id: layerBelow.id,
          layerWorkspaceId: layerBelow.layerWorkspaceId,
          renderOrder: layerBelow.renderOrder,
          timestamp: Date.now(),
          contentVersion: Date.now(),
          modificationLog: mergeModificationLogs(
            mergedLayerBase.modificationLog,
            originalBelowLayerLog,
            [createdFromEntry]
          ),
        };
        const prevLayers = listScopedLayers();
        const withoutTop = prevLayers.filter(layer => layer.id !== topLayer.id);
        const nextLayers = withoutTop.map((layer) => {
          if (layer.id === layerBelow.id) {
            return mergedLayerWithLog;
          }
          return layer;
        });
        const delta = computeLayerDelta(prevLayers, nextLayers);
        applyLayerDelta(delta);
        syncWorkspaceLayerMembership([workspaceId]);
        // The top layer leaves the canvas, its row stays, detached: the layer's own save writes nothing for a row it never created.
        kernel.current.queueLayerSave(topLayer.id, { workspaceId: null });
        kernel.current.invalidateWorkspaceVersionCache(topLayer.layerWorkspaceId);

        if (!hasEmittedMergeTimelineEvent) {
          hasEmittedMergeTimelineEvent = true;
          const workspace = kernel.current.getWorkspace(topLayer.layerWorkspaceId ?? '');
          const workspaceName =
            typeof workspace?.name === 'string' && workspace.name.trim().length > 0
              ? workspace.name.trim()
              : null;
          emitStructuredStudioMutationEvent({
            projectId,
            workspaceId: topLayer.layerWorkspaceId,
            eventType: 'STUDIO_LAYER_MERGED',
            payload: {
              workspaceName,
              sourceLayerId: topLayer.id,
              sourceLayerName: topLayer.name ?? null,
              targetLayerId: layerBelow.id,
              targetLayerName: layerBelow.name ?? null,
              resultLayerId: layerBelow.id,
              resultLayerName: mergedLayerWithLog.name ?? layerBelow.name ?? null,
              mergeMode: 'MERGE_DOWN',
            },
          });
        }
      },
      undo: () => {
        if (!kernel.current || !mergedLayerBase) {
          console.error("Cannot undo merge: merged layer data is missing.");
          return;
        }
        const prevLayers = listScopedLayers();
        const restoredBelow = prevLayers.map((layer) => {
          if (layer.id === layerBelow.id) {
            return {
              ...layerBelow,
              modificationLog: cloneModificationLog(originalBelowLayerLog),
            };
          }
          return layer;
        });
        const result = [...restoredBelow];
        result.splice(topLayerIndex, 0, {
          ...topLayer,
          modificationLog: cloneModificationLog(originalTopLayerLog),
        });
        const delta = computeLayerDelta(prevLayers, result);
        applyLayerDelta(delta);
        syncWorkspaceLayerMembership([workspaceId]);
        kernel.current.invalidateWorkspaceVersionCache(topLayer.layerWorkspaceId);
      },
    });
  }, [kernel, mergeLayersCanvas, applyLayerDelta, computeLayerDelta, getLayerById, listLayersByProjectId, getLayersInWorkspaceContext, syncWorkspaceLayerMembership, emitStructuredStudioMutationEvent]);


  const getLayerRowBoundingBox = useCallback(async (layer: Layer): Promise<{ minX: number, minY: number, maxX: number, maxY: number } | null> => {
    if (!kernel.current) return null;

    if (!layer.imageData || !layer.visible) {
      return null;
    }
    const workspace = kernel.current.getWorkspace(layer.layerWorkspaceId ?? '');
    if (!workspace) {
        console.warn(`getLayerRowBoundingBox: Workspace ${layer.layerWorkspaceId} not found for layer ${layer.id}. Cannot calculate world coordinates.`);
        return null;
    }
    const frameRect = workspace.frameRect;
    const { imageData, scale: layerScale = 1 } = layer;
    const originalWidth = imageData.width;
    const originalHeight = imageData.height;
    let cMinX = Infinity, cMinY = Infinity, cMaxX = -Infinity, cMaxY = -Infinity;
    let hasContent = false;
    for (let iy = 0; iy < originalHeight; iy++) {
      for (let ix = 0; ix < originalWidth; ix++) {
        const pixelIndex = (iy * originalWidth + ix) * 4;
        if ((imageData.data[pixelIndex + 3] || 0) > 0) {
          hasContent = true;
          cMinX = Math.min(cMinX, ix * layerScale);
          cMinY = Math.min(cMinY, iy * layerScale);
          cMaxX = Math.max(cMaxX, (ix + 1) * layerScale);
          cMaxY = Math.max(cMaxY, (iy + 1) * layerScale);
        }
      }
    }
    if (!hasContent) return null;
    const layerWorldRect = {
      x: frameRect.x + layer.rect.x,
      y: frameRect.y + layer.rect.y,
      w: originalWidth * layerScale,
      h: originalHeight * layerScale,
    };
    const contentWorldRect = projectStudioLayerLocalRectToWorld(
      { x: cMinX, y: cMinY, w: cMaxX - cMinX, h: cMaxY - cMinY },
      layerWorldRect,
      resolveStudioLayerTransform(layer.params),
    );
    return {
      minX: contentWorldRect.x,
      minY: contentWorldRect.y,
      maxX: contentWorldRect.x + contentWorldRect.w,
      maxY: contentWorldRect.y + contentWorldRect.h,
    };
  }, [kernel]);


  const calculateUnionRectOfLayers = useCallback(async (
    layersToConsider: Layer[]
  ): Promise<{ unionRect: Rect; contributingLayers: Layer[] } | null> => {
    let unionMinX_world = Infinity;
    let unionMinY_world = Infinity;
    let unionMaxX_world = -Infinity;
    let unionMaxY_world = -Infinity;
    const contributingLayers: Layer[] = [];

    for (const layer of layersToConsider) {
      // The layers passed here are assumed to be already filtered for visibility, workspace, etc., by the caller if needed.
      const bbox = await getLayerRowBoundingBox(layer);
      if (bbox) {
        contributingLayers.push(layer);
        unionMinX_world = Math.min(unionMinX_world, bbox.minX);
        unionMinY_world = Math.min(unionMinY_world, bbox.minY);
        unionMaxX_world = Math.max(unionMaxX_world, bbox.maxX);
        unionMaxY_world = Math.max(unionMaxY_world, bbox.maxY);
      }
    }

    if (contributingLayers.length === 0) {
      return null; // No layers contributed to the bounding box
    }

    return {
      unionRect: {
        x: unionMinX_world,
        y: unionMinY_world,
        w: Math.max(0.001, unionMaxX_world - unionMinX_world),
        h: Math.max(0.001, unionMaxY_world - unionMinY_world),
      },
      contributingLayers,
    };
  }, [getLayerRowBoundingBox]);


  const getCombinedVisibleContentBoundingBox = useCallback(async (workspaceId: string): Promise<Rect | null> => {
    if (!kernel.current) return null;
    const visibleLayersInWorkspace = getLayersInWorkspaceContext(workspaceId).filter((layer) => layer.visible);

    const result = await calculateUnionRectOfLayers(visibleLayersInWorkspace);
    return result ? result.unionRect : null;
  }, [kernel, calculateUnionRectOfLayers, getLayersInWorkspaceContext]);


  const duplicateLayers = useCallback(async (workspaceId: string, layerNamesOrIds: string[]): Promise<void> => {
    if (!kernel.current) return;
    const projectId = requireWorkspaceProjectIdFromKernel(kernel, workspaceId, 'useLayers');
    const currentLayers = projectId
      ? listLayersByProjectId(projectId)
      : getLayersInWorkspaceContext(workspaceId);
    const workspaceLayers = getLayersInWorkspaceContext(workspaceId);
    const targetWorkspaceLayerIds = new Set(
      workspaceLayers
        .filter((layer) =>
          layerNamesOrIds.some(
            (identifier) => identifier === layer.id.toString() || (layer.name && identifier === layer.name)
          )
        )
        .map((layer) => layer.id)
    );
    const newLayersArray: Layer[] = [];
    const duplicatedLayers: Layer[] = [];

    currentLayers.forEach(originalLayer => {
      newLayersArray.push(originalLayer);

      if (targetWorkspaceLayerIds.has(originalLayer.id)) {
        let newLayerName = '';
        const originalName = originalLayer.name || `Layer ${originalLayer.id}`;
        const numberSuffixRegex = /(?: )(\d+)$/;
        const numberMatch = originalName.match(numberSuffixRegex);

        if (numberMatch) {
          const baseName = originalName.substring(0, numberMatch.index);
          const number = parseInt(numberMatch[1] || '', 10);
          newLayerName = `${baseName} ${number + 1}`;
        } else {
          const letterSuffixRegex = /[a-zA-Z]$/;
          if (letterSuffixRegex.test(originalName) || !/\d$/.test(originalName)) {
            newLayerName = `${originalName} 2`;
          } else {
            newLayerName = `${originalName} Copy`;
          }
        }

        let finalNewName = newLayerName;
        let copyCount = 2;
        let nameCheckBase = originalName;
        if (numberMatch) {
          nameCheckBase = originalName.substring(0, numberMatch.index);
        }

        while (
          workspaceLayers.some(l => l.name === finalNewName) ||
          newLayersArray.slice(currentLayers.length).some(nl => nl.name === finalNewName)
        ) {
          if (numberMatch) {
            finalNewName = `${nameCheckBase} ${parseInt(numberMatch[1] || '', 10) + copyCount}`;
          } else if (originalName.endsWith(` ${copyCount - 1}`)) {
            finalNewName = `${originalName.substring(0, originalName.lastIndexOf(' '))} ${copyCount}`;
          } else {
            finalNewName = `${originalName} ${copyCount}`;
          }
          copyCount++;
        }

        const newId = createLayerId();
        const duplicatedLayer: Layer = syncRasterLayerRectSize({
          ...originalLayer,
          id: newId,
          timestamp: Date.now(),
          name: finalNewName,
          contentVersion: Date.now(),
          layerWorkspaceId: workspaceId,
          renderOrder: originalLayer.renderOrder,
        });
        newLayersArray.push(duplicatedLayer);
        duplicatedLayers.push(duplicatedLayer);
      }
    });

    const resequenced = resequenceRenderOrderByArray(newLayersArray, [workspaceId]);
    const delta = computeLayerDelta(currentLayers, resequenced);
    applyLayerDelta(delta);
    const workspaceLayerIdsAfter = resequenced
      .filter((layer) => layer.layerWorkspaceId === workspaceId)
      .map((layer) => layer.id);
    const workspaceProjectId = requireWorkspaceProjectIdFromKernel(kernel, workspaceId, 'useLayers');
    kernel.current.setWorkspace(workspaceId, (workspace) => ({
      ...workspace,
      layerIds: [...workspaceLayerIdsAfter],
    }), workspaceProjectId);

    kernel.current.invalidateWorkspaceVersionCache(workspaceId);
    duplicatedLayers.forEach((layer) => {
      persistLayerCreate(layer, 'useLayers.duplicateLayers');
    });
  }, [kernel, applyLayerDelta, computeLayerDelta, listLayersByProjectId, getLayersInWorkspaceContext, persistLayerCreate]);

  const updateSelectedLayers = useCallback(async (
    workspaceId: string,
    layerNamesOrIdsToProcess: string[],
    mode: 'REPLACE' | 'ADD' | 'REMOVE'
  ): Promise<void> => {
    if (!kernel.current) return;
    const projectId = requireWorkspaceProjectIdFromKernel(kernel, workspaceId, 'useLayers');
    if (layerNamesOrIdsToProcess.length === 0) {
      setProjectSelectedLayersId(kernel.current, projectId, []);
      return;
    }

    const currentLayers = getLayersInWorkspaceContext(workspaceId);

    // Resolve names/IDs to actual layer IDs from the current workspace
    const resolvedIdsToUpdate: string[] = [];
    if (layerNamesOrIdsToProcess && layerNamesOrIdsToProcess.length > 0) {
      for (const identifier of layerNamesOrIdsToProcess) {
        const foundLayer = currentLayers.find(l =>
          (l.id.toString() === identifier || (l.name && l.name === identifier))
        );
        if (foundLayer) {
          resolvedIdsToUpdate.push(foundLayer.id);
        } else {
          console.warn(`updateSelectedLayers: Layer with identifier "${identifier}" not found in active workspace "${workspaceId}". Skipping.`);
        }
      }
    }

    if (mode === 'REPLACE') {
      setProjectSelectedLayersId(kernel.current, projectId, resolvedIdsToUpdate);
    } else if (mode === 'ADD') {
      setProjectSelectedLayersId(kernel.current, projectId, (prevSelectedIds) => {
        const newSet = new Set([...prevSelectedIds, ...resolvedIdsToUpdate]);
        return Array.from(newSet);
      });
    } else if (mode === 'REMOVE') {
      setProjectSelectedLayersId(kernel.current, projectId, (prevSelectedIds) =>
        prevSelectedIds.filter(id => !resolvedIdsToUpdate.includes(id))
      );
    }
  }, [kernel, getLayersInWorkspaceContext]);

  const activeBlendRequestByLayerIdRef = useRef(new Map<string, string>());
  // Cancel invalidates pending async raster work before it can enter executeAction.
  const nativeLayerCutCancellationVersionRef = useRef(0);

  const blendLayer = useCallback(async (
    layerToBlend: Layer,
    fadeRadius: number = DEFAULT_BLEND_RADIUS_PX,
    isUndoable: boolean = true,
    cut?: { polygons: CropPoint[][]; invert: boolean },
  ): Promise<Layer | void> => {
    if (!kernel.current) {
      throw new Error("Kernel not available for blending.");
    }
    const activeBlendRequestByLayerId = activeBlendRequestByLayerIdRef.current;

    const shouldLogBlendPerf = kernel.current.getUserPreferences()?.performanceHudEnabled === true;
    const perfStart = now();

    if (!layerToBlend || !layerToBlend.imageData) {
      kernel.current.handleError("Layer to blend not found or has no image data.");
      return;
    }
    if (!layerToBlend.layerWorkspaceId) {
      kernel.current.handleError('Project layers cannot be blended in a workspace context.');
      return;
    }

    const workspace = kernel.current.getWorkspace(layerToBlend.layerWorkspaceId ?? '');

    const requestToken = `blend-${layerToBlend.id}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const requestContentVersion = layerToBlend.contentVersion;
    activeBlendRequestByLayerId.set(layerToBlend.id, requestToken);

    try {
      const raster = await runLayerRaster(layerToBlend.id, layerToBlend.imageData, {
        blendRadius: fadeRadius,
        placement: {
          rect: layerToBlend.rect,
          scale: layerToBlend.scale ?? 1,
          transform: resolveStudioLayerTransform(layerToBlend.params),
          frameRect: workspace?.frameRect,
        },
        cut,
      });
      const { executionPath } = raster;
      const blendedImageData = raster.result;

      if (!kernel.current || !blendedImageData) return;
      if (activeBlendRequestByLayerId.get(layerToBlend.id) !== requestToken) {
        if (shouldLogBlendPerf) {
          console.info('[Perf][blendLayer] ignored (superseded request)', {
            layerId: layerToBlend.id,
            workspaceId: layerToBlend.layerWorkspaceId,
            contentVersion: requestContentVersion,
            executionPath,
            timingsMs: {
              worker: roundMs(raster.workerMs),
              fallbackMain: roundMs(raster.fallbackMainMs),
              total: roundMs(now() - perfStart),
            },
          });
        }
        return;
      }

      const latestLayer = getLayerById(layerToBlend.id);
      if (!latestLayer || !latestLayer.imageData) {
        if (shouldLogBlendPerf) {
          console.info('[Perf][blendLayer] ignored (layer missing)', {
            layerId: layerToBlend.id,
            workspaceId: layerToBlend.layerWorkspaceId,
            contentVersion: requestContentVersion,
            executionPath,
            timingsMs: {
              worker: roundMs(raster.workerMs),
              fallbackMain: roundMs(raster.fallbackMainMs),
              total: roundMs(now() - perfStart),
            },
          });
        }
        return;
      }
      if (latestLayer.contentVersion !== requestContentVersion) {
        console.info(`[BlendLayer] Ignored stale blend result for layer ${layerToBlend.id}.`);
        if (shouldLogBlendPerf) {
          console.info('[Perf][blendLayer] ignored (stale contentVersion)', {
            layerId: layerToBlend.id,
            workspaceId: layerToBlend.layerWorkspaceId,
            expectedContentVersion: requestContentVersion,
            actualContentVersion: latestLayer.contentVersion,
            executionPath,
            timingsMs: {
              worker: roundMs(raster.workerMs),
              fallbackMain: roundMs(raster.fallbackMainMs),
              total: roundMs(now() - perfStart),
            },
          });
        }
        return;
      }

      const targetLayerId = latestLayer.id;
      const targetWorkspaceId = latestLayer.layerWorkspaceId;
      const originalLayerForUndo = {
        imageData: latestLayer.imageData,
        contentVersion: latestLayer.contentVersion,
        imageSrc: latestLayer.imageSrc,
        modificationLog: cloneModificationLog(latestLayer.modificationLog),
      };
      const newLayerData = {
        imageData: blendedImageData,
        contentVersion: Date.now(),
        imageSrc: undefined,
      };

      if (executionPath === 'worker' && shouldLogBlendPerf) {
        // Worker path is the expected fast path.
        console.log('[Perf][blendLayer] done', {
          layerId: layerToBlend.id,
          workspaceId: layerToBlend.layerWorkspaceId,
          size: `${blendedImageData.width}x${blendedImageData.height}`,
          fadeRadius,
          isUndoable,
          executionPath,
          timingsMs: {
            worker: roundMs(raster.workerMs),
            total: roundMs(now() - perfStart),
          },
        });
      } else if (shouldLogBlendPerf) {
        console.log('[Perf][blendLayer] done (fallback main-thread)', {
          layerId: layerToBlend.id,
          workspaceId: layerToBlend.layerWorkspaceId,
          size: `${blendedImageData.width}x${blendedImageData.height}`,
          fadeRadius,
          isUndoable,
          executionPath,
          workerError: raster.workerError instanceof Error ? raster.workerError.message : String(raster.workerError ?? 'unknown'),
          timingsMs: {
            worker: roundMs(raster.workerMs),
            fallbackMain: roundMs(raster.fallbackMainMs),
            total: roundMs(now() - perfStart),
          },
        });
      }

      if (isUndoable) {
        kernel.current.executeAction({
          description: `Feather Edges: ${latestLayer.name}`,
          redo: () => {
            if (!kernel.current) return;
            const timestamp = new Date().toISOString();
            const logEntry: LayerModificationLogEntry = { timestamp, type: 'MANUAL_FEATHER' };
            kernel.current.setLayer(targetLayerId, (existingLayer) => ({
              ...existingLayer,
              ...newLayerData,
              modificationLog: mergeModificationLogs(existingLayer.modificationLog, originalLayerForUndo.modificationLog, [logEntry]),
            }));
            kernel.current.invalidateWorkspaceVersionCache(targetWorkspaceId);
          },
          undo: () => {
            if (!kernel.current) return;
            kernel.current.setLayer(targetLayerId, (existingLayer) => ({
              ...existingLayer,
              ...originalLayerForUndo,
              modificationLog: cloneModificationLog(originalLayerForUndo.modificationLog),
            }));
            kernel.current.invalidateWorkspaceVersionCache(targetWorkspaceId);
          },
        });
        return;
      }

      const logEntry: LayerModificationLogEntry = { timestamp: new Date().toISOString(), type: 'MANUAL_FEATHER' };
      return {
        ...latestLayer,
        ...newLayerData,
        modificationLog: mergeModificationLogs(latestLayer.modificationLog, undefined, [logEntry]),
      };
    } finally {
      if (activeBlendRequestByLayerId.get(layerToBlend.id) === requestToken) {
        activeBlendRequestByLayerId.delete(layerToBlend.id);
      }
    }
  }, [kernel, runLayerRaster]);

  const resolveLayerImageDataForCut = useCallback(async (layerToResolve: Layer): Promise<ImageData | null> => {
    if (layerToResolve.type === 'video') {
      return null;
    }
    if (layerToResolve.imageData) {
      return layerToResolve.imageData;
    }
    if (!layerToResolve.imageSrc) {
      return null;
    }
    try {
      return await loadImageDataFromUrl(layerToResolve.imageSrc);
    } catch (error) {
      console.error('[Layer Cut] Failed to hydrate layer image for cut:', error);
      return null;
    }
  }, []);

  const applyNativeLayerCut = useCallback(async (
    layerId: string,
    polygons: CropPoint[][],
    options: NativeLayerCutOptions,
  ): Promise<boolean> => {
    if (!kernel.current) return false;
    const appliedOptions: NativeLayerCutOptions = {
      blendRadius: clampNativeLayerCutBlendRadius(options?.blendRadius),
      invert: options?.invert === true,
    };

    const currentLayer = getLayerById(layerId);
    if (!currentLayer) {
      kernel.current.handleError('Layer not found.');
      return false;
    }
    if (currentLayer.type === 'video') {
      kernel.current.handleError('Cut is only available for image layers.');
      return false;
    }
    const projectId = requireWorkspaceProjectIdFromKernel(kernel, currentLayer.layerWorkspaceId ?? '', 'useLayers');

    const nativeLayerCutCancellationVersion = nativeLayerCutCancellationVersionRef.current;
    const sourceImageData = await resolveLayerImageDataForCut(currentLayer);
    if (nativeLayerCutCancellationVersionRef.current !== nativeLayerCutCancellationVersion) {
      return false;
    }
    if (!sourceImageData) {
      kernel.current.handleError('This layer has no image content to cut.');
      return false;
    }

    const cutBounds = getNativeLayerCutImageBounds(
      sourceImageData.width,
      sourceImageData.height,
      polygons,
      appliedOptions.invert,
    );
    if (!cutBounds) {
      kernel.current.handleError('Failed to compute cut polygon.');
      return false;
    }

    const scale = currentLayer.scale ?? 1;
    const workspace = kernel.current.getWorkspace(currentLayer.layerWorkspaceId ?? '');
    if (!workspace) {
      kernel.current.handleError('Workspace for layer not found.');
      return false;
    }
    const transform = resolveStudioLayerTransform(currentLayer.params);
    const layerWorldRect = {
      x: workspace.frameRect.x + currentLayer.rect.x,
      y: workspace.frameRect.y + currentLayer.rect.y,
      w: Number.isFinite(currentLayer.rect.w) && currentLayer.rect.w > 0
        ? currentLayer.rect.w
        : sourceImageData.width * scale,
      h: Number.isFinite(currentLayer.rect.h) && currentLayer.rect.h > 0
        ? currentLayer.rect.h
        : sourceImageData.height * scale,
    };
    const nextRect = appliedOptions.invert
      ? currentLayer.rect
      : buildStudioLayerRectPreservingTransformPivot(
          currentLayer.rect,
          {
            x: cutBounds.minX * scale,
            y: cutBounds.minY * scale,
          },
          cutBounds.width,
          cutBounds.height,
          scale,
          layerWorldRect,
          transform,
          workspace.frameRect,
        );

    const cut = { polygons, invert: appliedOptions.invert };
    let nextImageData: ImageData;
    let nextModificationLog = mergeModificationLogs(
      currentLayer.modificationLog,
      undefined,
      [{ timestamp: new Date().toISOString(), type: 'MANUAL_EXTRACT' }],
    );
    let nextContentVersion = (currentLayer.contentVersion || 0) + 1;
    if (appliedOptions.blendRadius > 0) {
      const blendedLayerState = await blendLayer(
        {
          ...currentLayer,
          imageData: sourceImageData,
          imageSrc: undefined,
          rect: nextRect,
        },
        appliedOptions.blendRadius,
        false,
        cut,
      );
      if (nativeLayerCutCancellationVersionRef.current !== nativeLayerCutCancellationVersion) {
        return false;
      }
      if (!blendedLayerState || !blendedLayerState.imageData) {
        kernel.current.handleError('Failed to blend cut result.');
        return false;
      }
      nextImageData = blendedLayerState.imageData;
      nextModificationLog = cloneModificationLog(blendedLayerState.modificationLog);
      nextContentVersion = blendedLayerState.contentVersion ?? nextContentVersion;
    } else {
      const raster = await runLayerRaster(layerId, sourceImageData, {
        blendRadius: 0,
        placement: { rect: nextRect, scale, transform, frameRect: workspace.frameRect },
        cut,
      });
      if (nativeLayerCutCancellationVersionRef.current !== nativeLayerCutCancellationVersion) {
        return false;
      }
      if (!raster.result) {
        kernel.current.handleError('Failed to compute cut polygon.');
        return false;
      }
      nextImageData = raster.result;
    }

    const previousLayerState = {
      imageData: currentLayer.imageData,
      imageSrc: currentLayer.imageSrc,
      rect: currentLayer.rect,
      loadError: currentLayer.loadError,
      contentVersion: currentLayer.contentVersion,
      modificationLog: cloneModificationLog(currentLayer.modificationLog),
    };

    const nextLayerState = {
      imageData: nextImageData,
      imageSrc: undefined,
      rect: nextRect,
      loadError: false,
      contentVersion: nextContentVersion,
      modificationLog: nextModificationLog,
    };

    const applyLayerPatch = (patch: Partial<Layer>) => {
      kernel.current?.setLayer(layerId, (existing) => ({ ...existing, ...patch }));
      kernel.current?.invalidateWorkspaceVersionCache(currentLayer.layerWorkspaceId);
    };

    if (nativeLayerCutCancellationVersionRef.current !== nativeLayerCutCancellationVersion) {
      return false;
    }

    let hasEmittedCutTimelineEvent = false;
    kernel.current.executeAction({
      description: `${appliedOptions.invert ? 'Cut Outside' : 'Cut'} Layer: ${currentLayer.name || `Layer ${currentLayer.id}`}`,
      redo: () => {
        applyLayerPatch(nextLayerState);
        kernel.current?.logUserAction('Layer Cut Applied', {
          layer_id: currentLayer.id,
          layer_name: currentLayer.name,
          auto_blended: appliedOptions.blendRadius > 0,
          blend_radius: appliedOptions.blendRadius,
          invert: appliedOptions.invert,
        });
        if (!hasEmittedCutTimelineEvent && kernel.current) {
          hasEmittedCutTimelineEvent = true;
          const latestLayer = getLayerById(layerId);
          const workspace = kernel.current.getWorkspace(currentLayer.layerWorkspaceId ?? '');
          const workspaceName =
            typeof workspace?.name === 'string' && workspace.name.trim().length > 0
              ? workspace.name.trim()
              : null;
          emitStructuredStudioMutationEvent({
            projectId,
            workspaceId: currentLayer.layerWorkspaceId,
            eventType: 'STUDIO_LAYER_CUT_VALIDATED',
            payload: {
              workspaceName,
              layerId: currentLayer.id,
              layerName: latestLayer?.name ?? currentLayer.name ?? null,
              polygonCount: Array.isArray(polygons) ? polygons.length : 0,
              blendRadius: appliedOptions.blendRadius,
              invert: appliedOptions.invert,
            },
          });
        }
      },
      undo: () => {
        applyLayerPatch(previousLayerState);
        kernel.current?.logUserAction('Layer Cut Undo', {
          layer_id: currentLayer.id,
          layer_name: currentLayer.name,
          blend_radius: appliedOptions.blendRadius,
          invert: appliedOptions.invert,
        });
      },
    });

    return true;
  }, [blendLayer, kernel, resolveLayerImageDataForCut, getLayerById, emitStructuredStudioMutationEvent, runLayerRaster]);

  const cancelNativeLayerCut = useCallback(() => {
    nativeLayerCutCancellationVersionRef.current += 1;
    if (!kernel.current) return;
    kernel.current.setNativeLayerCutSession(null);
    if (kernel.current.getActiveTool() === 'scissors') {
      kernel.current.setActiveTool(null);
    }
  }, [kernel]);

  const startNativeLayerCut = useCallback(async (layer: Layer, options?: { zoomToLayer?: boolean }) => {
    if (!kernel.current) return;

    const layerSnapshot = getLayerById(layer.id);
    if (!layerSnapshot) {
      kernel.current.handleError('Layer not found.');
      return;
    }
    if (!layerSnapshot.layerWorkspaceId) {
      kernel.current.handleError('Project layers cannot be cut in a workspace context.');
      return;
    }
    if (layerSnapshot.type === 'video') {
      kernel.current.handleError('Cut is only available for image layers.');
      return;
    }

    const sourceImageData = await resolveLayerImageDataForCut(layerSnapshot);
    if (!sourceImageData) {
      kernel.current.handleError('This layer has no image content to cut.');
      return;
    }

    if (!layerSnapshot.imageData) {
      kernel.current.setLayer(layerSnapshot.id, (current) => ({
        ...current,
        imageData: sourceImageData,
        imageSrc: current.imageSrc,
      }));
    }

    const projectId = requireWorkspaceProjectIdFromKernel(kernel, layerSnapshot.layerWorkspaceId ?? '', 'useLayers');
    setProjectSelectedLayersId(kernel.current, projectId, [layerSnapshot.id]);
    kernel.current.setScissorsHoverLayerId(null);
    kernel.current.setActiveTool('scissors');
    if (options?.zoomToLayer === true) {
      await kernel.current.zoomOnLayer(layerSnapshot.id);
    }

    kernel.current.logUserAction('Layer Cut Opened', {
      layer_id: layerSnapshot.id,
      layer_name: layerSnapshot.name,
      mode: 'native',
    });

    const language = kernel.current.getLanguage();
    const instruction =
      language === 'fr'
        ? 'Dessinez la zone à conserver, puis validez. Maintenez Alt pour voir en dessous.'
        : 'Draw the area to keep, then validate. Hold Alt to peek behind.';

    kernel.current.setNativeLayerCutSession({
      layerId: layerSnapshot.id,
      workspaceId: layerSnapshot.layerWorkspaceId,
      instruction,
      startedAt: Date.now(),
    });
  }, [kernel, resolveLayerImageDataForCut, getLayerById]);

  const cutLayer = useCallback(async (layer: Layer, options?: { zoomToLayer?: boolean }) => {
    await startNativeLayerCut(layer, options);
  }, [startNativeLayerCut]);


  return useMemo(() => ({
    loadLayer,
    updateLayerVisibility,
    hideBuriedLayers,
    updateLayerLock,
    removeLayer,
    updateLayerName,
    selectLayer,
    reorderLayer,
    copySelectedLayersToClipboard,
    restoreLayerConfig,
    downloadLayer,
    duplicateLayer,
    addLayerFromFile,
    addLayerFromUrl,
    addLayerFromImageData,
    addSvgLayer,
    mergeLayerDown,
    mergeLayersCanvas,
    mergeGeneratedLayerIntoAnchorLayer,
    paintLayerIntoGridSection,
    getCombinedVisibleContentBoundingBox,
    calculateUnionRectOfLayers,
    duplicateLayers,
    updateSelectedLayers,
    getLayersInWorkspaceContext,
    listLayersByProjectId,
    attachLayerToWorkspace,
    detachLayerFromWorkspace,
    bumpLayerPulseToken,
    alignLayerToUnderlying,
    extractChangesFromUnderlying,
    harmonizeLayerColor,
    harmonizeLayerUnchanged,
    integrateLayer,
    integrateGeneratedLayer,
    reloadLayerAsset,
    hydrateProjectLayerPreviews,
    hydrateVisibleLayers,
    blendLayer,
    cutLayer,
    startNativeLayerCut,
    applyNativeLayerCut,
    cancelNativeLayerCut,
  }), [
    loadLayer,
    updateLayerVisibility, hideBuriedLayers, updateLayerLock, removeLayer,
    updateLayerName, selectLayer, reorderLayer, copySelectedLayersToClipboard, restoreLayerConfig, downloadLayer,
    duplicateLayer, addLayerFromFile,
    addLayerFromUrl, addLayerFromImageData, addSvgLayer, mergeLayerDown, mergeLayersCanvas, mergeGeneratedLayerIntoAnchorLayer,
    getCombinedVisibleContentBoundingBox,
    calculateUnionRectOfLayers,
    duplicateLayers, updateSelectedLayers,
    getLayersInWorkspaceContext, listLayersByProjectId, attachLayerToWorkspace, detachLayerFromWorkspace, bumpLayerPulseToken, alignLayerToUnderlying,
    extractChangesFromUnderlying, harmonizeLayerColor, harmonizeLayerUnchanged, integrateLayer, integrateGeneratedLayer, reloadLayerAsset, hydrateProjectLayerPreviews, hydrateVisibleLayers,
    blendLayer, cutLayer, startNativeLayerCut, applyNativeLayerCut, cancelNativeLayerCut,
  ]);
};
